import imageService from './imageService';
import edgeFunctionService, { AiProxyError } from './edgeFunctionService';
import { getExtractionTypeOptions } from '../utils/extractionTaxonomy';
import { aliasMermaidBlocks, restoreMermaidAliases } from '../utils/mermaidUtils';
import {
  applyDocumentEditOperations as applyExactDocumentEditOperations,
  normalizeDocumentEditOperations,
} from '../utils/documentPatchUtils';
import {
  AI_TASKS,
  buildLegacyGenerationConfig,
  fnv1a32Hex,
  getDefaultProfileForTask,
  legacyModelNameForTask,
  normalizeTask,
  stableSeed,
  toGeminiResponseSchema,
} from '../constants/aiModels';
import { wrapFieldInstruction } from '../utils/templateFields';

export { AiProxyError, AiProxyError as AiTaskError };

/** Max length for `suggestedPrompt` after validation (templatization hints, not meta-essays). */
const TEMPLATE_SUGGESTED_PROMPT_MAX_LENGTH = 320;

/** Max chars of plain text sent alongside HTML for non-chunked Word analysis. */
const TEMPLATE_ANALYSIS_TEXT_MAX = 12000;

/** Chunked template analysis concurrency (rate-limit friendly). */
const ANALYSIS_PARALLEL = Object.freeze({ enabled: true, batchSize: 3, batchDelay: 2000 });

/** Separator used to alias many block fragments in one pass. */
const BLOCK_ALIAS_SEPARATOR = '\n<!--quill-block-separator-->\n';

/** Block edit operation names (CONTRACTS §6). */
const BLOCK_EDIT_OPS = Object.freeze(['replace_block', 'insert_after', 'insert_before', 'delete_block', 'replace_text']);

/** JSON schema for the legacy find/replace patch plan. */
const PATCH_PLAN_SCHEMA = {
  type: 'object',
  properties: {
    reasoning: { type: 'string', description: 'What was changed and why, 1-3 plain sentences.' },
    operations: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: ['replace', 'insert_after', 'insert_before', 'delete'] },
          search: { type: 'string', description: 'Exact contiguous substring copied from DOCUMENT HTML.' },
          replace: { type: 'string', description: 'Replacement or inserted HTML; empty for delete.' },
        },
        required: ['type', 'search', 'replace'],
        propertyOrdering: ['type', 'search', 'replace'],
      },
    },
  },
  required: ['reasoning', 'operations'],
  propertyOrdering: ['reasoning', 'operations'],
};

/** JSON schema for a block-id edit plan (CONTRACTS §6 ops). */
const BLOCK_PLAN_SCHEMA = {
  type: 'object',
  properties: {
    reasoning: { type: 'string', description: 'What was changed and why, 1-3 plain sentences.' },
    operations: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          op: { type: 'string', enum: BLOCK_EDIT_OPS },
          blockId: { type: 'string', description: 'Id of the target block from BLOCKS.' },
          html: { type: 'string', description: 'New block HTML for replace_block / insert_after / insert_before.' },
          find: { type: 'string', description: 'replace_text only: exact text inside the block.' },
          replace: { type: 'string', description: 'replace_text only: replacement text.' },
        },
        required: ['op'],
        propertyOrdering: ['op', 'blockId', 'html', 'find', 'replace'],
      },
    },
  },
  required: ['reasoning', 'operations'],
  propertyOrdering: ['reasoning', 'operations'],
};

/** JSON schema for full-HTML results (rewrite and first draft). */
const HTML_RESULT_SCHEMA = {
  type: 'object',
  properties: {
    reasoning: { type: 'string', description: 'One or two plain sentences, no HTML.' },
    html: { type: 'string', description: 'Complete document HTML.' },
  },
  required: ['reasoning', 'html'],
  propertyOrdering: ['reasoning', 'html'],
};

/** One structure row of a template analysis. */
const ANALYSIS_SECTION_SCHEMA = {
  type: 'object',
  properties: {
    level: { type: 'integer' },
    type: { type: 'string', enum: ['heading', 'content'] },
    title: { type: 'string' },
    htmlTag: { type: 'string', enum: ['h1', 'h2', 'h3', 'p', 'ul', 'ol', 'table'] },
    content: { type: 'string', description: 'Short grounding excerpt from the source (content rows).' },
    contentType: {
      type: 'string',
      enum: ['introduction', 'overview', 'detailed', 'list', 'table', 'conclusion'],
    },
    suggestedPrompt: {
      type: 'string',
      description: 'Content rows: what this slot must convey for a new project (max ~300 chars). Headings: empty.',
    },
    fieldKind: { type: 'string', enum: ['rich', 'list', 'table_rows', 'text'] },
    required: { type: 'boolean' },
    source: { type: 'string', enum: ['requirements', 'sessions', 'project', 'any'] },
    maxWords: { type: 'integer' },
    position: { type: 'integer' },
  },
  required: ['level', 'type', 'title', 'htmlTag', 'contentType', 'suggestedPrompt', 'position'],
  propertyOrdering: [
    'position', 'level', 'type', 'title', 'htmlTag', 'contentType', 'content',
    'suggestedPrompt', 'fieldKind', 'required', 'source', 'maxWords',
  ],
};

/** JSON schema for whole-document template analysis. */
const TEMPLATE_ANALYSIS_SCHEMA = {
  type: 'object',
  properties: {
    documentTitle: { type: 'string' },
    structure: { type: 'array', items: ANALYSIS_SECTION_SCHEMA },
    metadata: {
      type: 'object',
      properties: {
        complexity: { type: 'string', enum: ['low', 'medium', 'high'] },
        documentType: { type: 'string' },
        recommendedTemplateStructure: { type: 'string' },
      },
    },
  },
  required: ['documentTitle', 'structure'],
  propertyOrdering: ['documentTitle', 'structure', 'metadata'],
};

/** JSON schema for one chunk of a large-document analysis. */
const TEMPLATE_CHUNK_SCHEMA = {
  type: 'object',
  properties: { structure: { type: 'array', items: ANALYSIS_SECTION_SCHEMA } },
  required: ['structure'],
};

/** Shared rules telling the analyzer how to describe fill-in fields. */
const TEMPLATE_FIELD_RULES = `FIELD DESCRIPTION (content rows only):
- "fieldKind": "rich" for prose paragraphs, "list" for bullet/numbered lists, "table_rows" for tables, "text" for a short inline value.
- "required": false only when the section is clearly optional for many projects (e.g. appendices, optional notes); otherwise true.
- "source": where the facts come from at generation time: "requirements" (requirement list), "sessions" (meeting summaries/decisions/actions), "project" (project name/description), or "any".
- "maxWords": a sensible length limit for the slot, based on the gold excerpt length (e.g. 60-400).
- Never put client names, numbers or one-off facts from the gold document into suggestedPrompt; describe what to write, not what was written.`;

/**
 * Truncate templatization hint to a safe length; strip leading meta phrases and braces.
 * @param {string} text - Raw model output.
 * @param {number} [max] - Max length.
 * @returns {string} Truncated hint.
 */
function truncateTemplatizationHint(text, max = TEMPLATE_SUGGESTED_PROMPT_MAX_LENGTH) {
  if (!text || typeof text !== 'string') return '';
  let s = text.replace(/\s+/g, ' ').trim();
  const meta = /^(generate|draft|create|write|produce)\s+(a |an |the )?/i;
  while (meta.test(s)) s = s.replace(meta, '').trim();
  s = s.replace(/[{}]/g, '');
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * Escape text for HTML element content and attribute values.
 * @param {unknown} value - Raw value.
 * @returns {string} Escaped string.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Parse model JSON text: strict parse first, then fenced / embedded object or array.
 * @param {string} text - Model output.
 * @returns {object | Array | null} Parsed value or null.
 */
function parseJsonText(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const cleaned = text.trim().replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    /* fall through to embedded match */
  }
  const match = cleaned.match(/[[{][\s\S]*[\]}]/);
  if (!match) return null;
  try {
    return JSON.parse(match[0]);
  } catch {
    return null;
  }
}

/**
 * Recover HTML from a `{ "reasoning", "html" }` reply that failed to parse.
 * When the text is JSON-shaped, the "html" string value is located and JSON-decoded (so escaped
 * newlines and quotes become real characters); a value cut off before its closing quote is
 * reported as truncated. Text that is not JSON-shaped (an old proxy ignoring the schema and
 * answering in plain HTML) is cut at its first tag.
 * @param {string} rawText - Raw model output.
 * @returns {{ html: string, truncated: boolean } | null} Recovered HTML, or null when none.
 */
export function salvageHtmlFromJsonReply(rawText) {
  const text = String(rawText || '')
    .trim()
    .replace(/^```(?:json|html)?\s*\n?/i, '')
    .replace(/\n?```\s*$/i, '')
    .trim();
  if (!text) return null;
  const keyMatch = /"html"\s*:\s*"/.exec(text);
  if (keyMatch) {
    const start = keyMatch.index + keyMatch[0].length;
    let end = -1;
    for (let i = start; i < text.length; i += 1) {
      const ch = text[i];
      if (ch === '\\') {
        i += 1;
      } else if (ch === '"') {
        end = i;
        break;
      }
    }
    const truncated = end < 0;
    let body = truncated ? text.slice(start) : text.slice(start, end);
    if (truncated) {
      // The cut can land inside an escape: drop a partial \uXXXX or an odd trailing backslash.
      body = body.replace(/\\u[0-9a-fA-F]{0,3}$/, '');
      const trailing = body.match(/\\+$/);
      if (trailing && trailing[0].length % 2 === 1) body = body.slice(0, -1);
    }
    let decoded;
    try {
      // Raw newlines/tabs inside the string are invalid JSON; escape them before decoding.
      decoded = JSON.parse(`"${body.replace(/\r/g, '\\r').replace(/\n/g, '\\n').replace(/\t/g, '\\t')}"`);
    } catch {
      return null;
    }
    const html = String(decoded).trim();
    return html.includes('<') ? { html, truncated } : null;
  }
  if (text.startsWith('{') || text.startsWith('[')) return null;
  const firstTagIdx = text.indexOf('<');
  if (firstTagIdx < 0) return null;
  return { html: text.slice(firstTagIdx).trim(), truncated: false };
}

/**
 * Whether an error is a user/caller cancellation.
 * @param {unknown} error - Thrown value.
 * @returns {boolean} True for aborts.
 */
function isAbortError(error) {
  return error?.code === 'ABORTED' || error?.name === 'AbortError';
}

/**
 * Plain text of an HTML fragment (for validation of replace_text ops).
 * @param {string} html - HTML.
 * @returns {string} Text.
 */
function htmlToText(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

/**
 * Count non-overlapping occurrences of `needle` in `haystack`.
 * @param {string} haystack - Text to search.
 * @param {string} needle - Substring.
 * @returns {number} Occurrences.
 */
function countOccurrences(haystack, needle) {
  if (!needle) return 0;
  return String(haystack).split(needle).length - 1;
}

/**
 * Client-side AI service. All model calls go through {@link AIService#runTask} /
 * {@link AIService#streamTask}, which send a task id to gemini-proxy v2 (the server picks the
 * model from task + account tier) and fall back to the v1 request shape on an old proxy.
 */
class AIService {
  constructor() {
    /** Legacy settings snapshot; only `activeAccountId` and optional sampling hints are read. */
    this.settings = null;
    /** Account used for tier resolution when a call does not pass `accountId`. */
    this.defaultAccountId = null;
  }

  /**
   * Store legacy settings (kept for callers that still inject `useSettings` state). Model fields
   * are ignored: the server resolves the model per task. Only `activeAccountId` is used, as the
   * default account for calls that do not pass one.
   * @param {object} settings - Settings state; may include `activeAccountId`.
   * @returns {void}
   */
  setSettings(settings) {
    this.settings = settings || null;
    if (settings && typeof settings.activeAccountId === 'string' && settings.activeAccountId) {
      this.defaultAccountId = settings.activeAccountId;
    }
  }

  /**
   * Set the default account used for tier resolution.
   * @param {string | null} accountId - Active account id.
   * @returns {void}
   */
  setActiveAccountId(accountId) {
    this.defaultAccountId = accountId || null;
  }

  /**
   * Resolve the account id for a call.
   * @param {string} [accountId] - Explicit account id.
   * @returns {string | undefined} Account id.
   */
  resolveAccountId(accountId) {
    return accountId || this.defaultAccountId || undefined;
  }

  /**
   * Flatten native contents into one prompt for the v1 proxy (single-turn only).
   * @param {Array<{role: string, parts: Array<object>}>} contents - Native contents.
   * @returns {string} Transcript text.
   */
  flattenContents(contents) {
    if (!Array.isArray(contents)) return '';
    return contents
      .map((content) => {
        const role = content?.role === 'model' ? 'ASSISTANT' : 'USER';
        const text = (Array.isArray(content?.parts) ? content.parts : [])
          .map((part) => {
            if (typeof part?.text === 'string' && part.thought !== true) return part.text;
            if (part?.functionCall) return `[Tool call ${part.functionCall.name}] ${JSON.stringify(part.functionCall.args || {})}`;
            if (part?.functionResponse) {
              return `[Tool result ${part.functionResponse.name}] ${JSON.stringify(part.functionResponse.response ?? null)}`;
            }
            return '';
          })
          .filter(Boolean)
          .join('\n');
        return text ? `${role}: ${text}` : '';
      })
      .filter(Boolean)
      .join('\n\n');
  }

  /**
   * Build a v2 proxy request body.
   * @param {object} p - Normalized task params.
   * @returns {object} Request body.
   */
  buildV2Body(p) {
    const generationConfig = { ...(p.generationConfig || {}) };
    if (p.responseSchema) {
      generationConfig.responseSchema = toGeminiResponseSchema(p.responseSchema);
    }
    return {
      task: p.task,
      profile: p.profile,
      ...(p.prompt ? { prompt: p.prompt } : {}),
      ...(Array.isArray(p.contents) && p.contents.length ? { contents: p.contents } : {}),
      ...(p.systemInstruction ? { systemInstruction: p.systemInstruction } : {}),
      ...(Array.isArray(p.parts) && p.parts.length ? { parts: p.parts } : {}),
      ...(Array.isArray(p.tools) && p.tools.length ? { tools: p.tools } : {}),
      ...(p.toolConfig ? { toolConfig: p.toolConfig } : {}),
      ...(typeof p.seed === 'number' && Number.isFinite(p.seed) ? { seed: p.seed } : {}),
      ...(p.accountId ? { accountId: p.accountId } : {}),
      generationConfig,
    };
  }

  /**
   * Build a v1 proxy request body (legacy `modelName`, complete generationConfig).
   * @param {object} p - Normalized task params.
   * @returns {{ body: object, model: string }} Body and the legacy model id it requests.
   */
  buildLegacyBody(p) {
    const model = legacyModelNameForTask(p.task);
    let prompt = p.prompt || '';
    if (Array.isArray(p.contents) && p.contents.length) {
      const transcript = this.flattenContents(p.contents);
      prompt = prompt ? `${transcript}\n\nUSER: ${prompt}` : transcript;
    }
    return {
      model,
      body: {
        prompt,
        systemInstruction: p.systemInstruction || '',
        modelName: model,
        ...(p.accountId ? { accountId: p.accountId } : {}),
        ...(Array.isArray(p.parts) && p.parts.length ? { parts: p.parts } : {}),
        generationConfig: buildLegacyGenerationConfig({
          profile: p.profile,
          model,
          overrides: p.generationConfig,
          seed: p.seed,
          responseSchema: p.responseSchema,
        }),
      },
    };
  }

  /**
   * Normalize runTask/streamTask options.
   * @param {object} options - Raw options.
   * @returns {object} Normalized params.
   */
  normalizeTaskParams(options) {
    const task = normalizeTask(options.task);
    return {
      ...options,
      task,
      profile: options.profile || getDefaultProfileForTask(task),
      systemInstruction: options.systemInstruction || '',
      accountId: this.resolveAccountId(options.accountId),
    };
  }

  /**
   * Parse a JSON task result; null when the text is not JSON.
   * @param {string} text - Model text.
   * @returns {object | Array | null} Parsed JSON.
   */
  parseTaskJson(text) {
    return parseJsonText(text);
  }

  /**
   * Run one non-streaming AI task (CONTRACTS §4). Never reads or mutates model settings.
   * @param {object} options - Task options.
   * @param {string} options.task - Task id from AI_TASKS (unknown → 'util').
   * @param {string} [options.prompt] - Single-turn prompt.
   * @param {Array<object>} [options.contents] - Native multi-turn contents.
   * @param {string} [options.systemInstruction] - System instruction.
   * @param {string} [options.profile] - Generation profile (default per task).
   * @param {number} [options.seed] - Deterministic seed (see stableSeed).
   * @param {object} [options.responseSchema] - JSON schema; the result then includes `json`.
   * @param {object} [options.generationConfig] - Whitelisted overrides (temperature, topP, maxOutputTokens, thinkingConfig).
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @param {string} [options.accountId] - Account for tier resolution.
   * @param {Array<object>} [options.parts] - Extra media parts for the prompt turn.
   * @param {Array<object>} [options.tools] - Function declarations (v2 only).
   * @param {object} [options.toolConfig] - Function calling config (v2 only).
   * @returns {Promise<{text: string, json?: object, model: string|null, finishReason: string, functionCalls: Array<object>, usage: object|null, modelContent: object|null, proxyVersion: number}>} Result.
   * @throws {AiProxyError} Typed error (code MAX_TOKENS, SAFETY, INVALID_JSON, ...).
   */
  async runTask(options = {}) {
    const p = this.normalizeTaskParams(options);
    if (!p.prompt && !(Array.isArray(p.contents) && p.contents.length)) {
      throw new AiProxyError('A prompt or contents is required.', { code: 'BAD_REQUEST' });
    }
    const caps = await edgeFunctionService.getProxyCapabilities();
    let proxyVersion = Number(caps?.proxyVersion) || 1;
    let response;
    let legacyModel = null;

    if (proxyVersion >= 2) {
      try {
        response = await edgeFunctionService.callGeminiProxy(this.buildV2Body(p), { signal: p.signal });
      } catch (error) {
        if (!/prompt is required/i.test(String(error?.message || ''))) throw error;
        // Proxy was rolled back mid-session: retry once with the v1 shape.
        edgeFunctionService.resetProxyCapabilities();
        proxyVersion = 1;
      }
    }
    if (proxyVersion < 2) {
      if (Array.isArray(p.tools) && p.tools.length) {
        throw new AiProxyError('Tool calling needs the updated AI proxy.', { code: 'TOOLS_UNSUPPORTED' });
      }
      const legacy = this.buildLegacyBody(p);
      legacyModel = legacy.model;
      response = await edgeFunctionService.callGeminiProxy(legacy.body, { signal: p.signal });
    }

    const text = typeof response?.text === 'string' ? response.text : '';
    const result = {
      text,
      model: response?.model || legacyModel,
      finishReason: response?.finishReason || 'STOP',
      functionCalls: Array.isArray(response?.functionCalls) ? response.functionCalls : [],
      usage: response?.usage ?? null,
      modelContent: response?.modelContent ?? null,
      proxyVersion,
    };
    if (p.responseSchema) {
      const json = this.parseTaskJson(text);
      if (json === null || typeof json !== 'object') {
        throw new AiProxyError('The AI returned malformed JSON. Please try again.', {
          code: 'INVALID_JSON',
          model: result.model,
          finishReason: result.finishReason,
          details: { text },
        });
      }
      result.json = json;
    }
    return result;
  }

  /**
   * Stream one AI task (CONTRACTS §4). `onEvent` receives NDJSON-shaped events:
   * meta, thought, text (delta), functionCall, done, error.
   * @param {object} options - Same as runTask (no responseSchema) plus `onEvent`.
   * @param {function(object): void} [options.onEvent] - Event callback.
   * @returns {Promise<{text: string, functionCalls: Array<object>, model: string|null, finishReason: string|null, usage: object|null, modelContent: object|null, thought: string}>} Aggregate.
   * @throws {AiProxyError} On failure or an error event (after emitting it).
   */
  async streamTask(options = {}) {
    const p = this.normalizeTaskParams(options);
    const onEvent = typeof options.onEvent === 'function' ? options.onEvent : () => {};
    if (!p.prompt && !(Array.isArray(p.contents) && p.contents.length)) {
      throw new AiProxyError('A prompt or contents is required.', { code: 'BAD_REQUEST' });
    }
    const caps = await edgeFunctionService.getProxyCapabilities();
    if (Number(caps?.proxyVersion) >= 2) {
      const res = await edgeFunctionService.streamGeminiProxy(this.buildV2Body(p), {
        signal: p.signal,
        onEvent,
      });
      return {
        text: res.text,
        functionCalls: res.functionCalls,
        model: res.model,
        finishReason: res.finishReason,
        usage: res.usage,
        modelContent: res.modelContent,
        thought: res.thought,
      };
    }

    if (Array.isArray(p.tools) && p.tools.length) {
      const error = new AiProxyError('Tool calling needs the updated AI proxy.', { code: 'TOOLS_UNSUPPORTED' });
      onEvent({ type: 'error', message: error.message, code: error.code });
      throw error;
    }
    const legacy = this.buildLegacyBody(p);
    onEvent({ type: 'meta', model: legacy.model, proxyVersion: 1, task: p.task });
    const res = await edgeFunctionService.streamGeminiProxy(legacy.body, {
      signal: p.signal,
      onEvent: (event) => {
        if (event?.type !== 'meta') onEvent(event);
      },
    });
    let text = res.text;
    if (!text) {
      // v1 streams can come back empty; retry once without streaming.
      const fallback = await edgeFunctionService.callGeminiProxy(legacy.body, { signal: p.signal });
      text = typeof fallback?.text === 'string' ? fallback.text : '';
      if (text) onEvent({ type: 'text', text });
    }
    if (!text) {
      const error = new AiProxyError('Model returned a response with no extractable text.', {
        code: 'EMPTY_RESPONSE',
        model: legacy.model,
      });
      onEvent({ type: 'error', message: error.message, code: error.code });
      throw error;
    }
    onEvent({ type: 'done', finishReason: 'STOP', usage: null, model: legacy.model });
    return {
      text,
      functionCalls: [],
      model: legacy.model,
      finishReason: 'STOP',
      usage: null,
      modelContent: { role: 'model', parts: [{ text }] },
      thought: res.thought,
    };
  }

  /**
   * Legacy text call kept for older callers. Delegates to runTask.
   * @param {string} prompt - User prompt text.
   * @param {string} [systemInstruction] - System instruction text.
   * @param {object} [options] - Options.
   * @param {string} [options.task] - Task id (default 'util').
   * @param {object} [options.generationConfig] - Overrides.
   * @param {string} [options.accountId] - Account for tier resolution.
   * @param {number} [options.seed] - Seed.
   * @param {object} [options.responseSchema] - JSON schema.
   * @param {string} [options.profile] - Profile.
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @returns {Promise<string>} Model response text.
   */
  async callGemini(prompt, systemInstruction = '', options = {}) {
    const result = await this.runTask({
      task: options.task || AI_TASKS.UTIL,
      prompt,
      systemInstruction,
      profile: options.profile,
      seed: options.seed,
      responseSchema: options.responseSchema,
      generationConfig: options.generationConfig,
      accountId: options.accountId,
      signal: options.signal,
      parts: options.parts,
    });
    return result.text;
  }

  /**
   * Simple text generation (editor inline actions). Delegates to runTask; honors temperature 0.
   * Seeded from task + instruction + prompt so the same request yields the same answer; pass a
   * `variant` (or explicit seed) to ask for a different one.
   * @param {string} prompt - Prompt text.
   * @param {string | object} [opts] - System instruction string (legacy) or options.
   * @param {string} [opts.systemInstruction] - System instruction.
   * @param {string} [opts.task] - Task id (default 'editor.inline').
   * @param {string} [opts.profile] - Profile (default per task).
   * @param {number} [opts.temperature] - Temperature override (0 allowed).
   * @param {number} [opts.topP] - topP override.
   * @param {number} [opts.maxOutputTokens] - Output budget.
   * @param {number} [opts.seed] - Explicit seed.
   * @param {number} [opts.variant] - Regenerate counter mixed into the default seed.
   * @param {string} [opts.accountId] - Account for tier resolution.
   * @param {AbortSignal} [opts.signal] - Abort signal.
   * @returns {Promise<string>} Generated text.
   */
  async generateText(prompt, opts = {}) {
    const options = typeof opts === 'string' ? { systemInstruction: opts } : (opts || {});
    const task = options.task || AI_TASKS.EDITOR_INLINE;
    const systemInstruction = options.systemInstruction || '';
    const generationConfig = {};
    const temperature = options.temperature ?? this.settings?.temperature;
    const topP = options.topP ?? this.settings?.topP;
    const maxOutputTokens = options.maxOutputTokens ?? this.settings?.maxOutputTokens;
    if (typeof temperature === 'number') generationConfig.temperature = temperature;
    if (typeof topP === 'number') generationConfig.topP = topP;
    if (typeof maxOutputTokens === 'number') generationConfig.maxOutputTokens = maxOutputTokens;
    const seed = typeof options.seed === 'number'
      ? options.seed
      : stableSeed(task, systemInstruction, prompt, options.variant ?? 0);
    const result = await this.runTask({
      task,
      profile: options.profile,
      prompt,
      systemInstruction,
      seed,
      generationConfig,
      accountId: options.accountId,
      signal: options.signal,
    });
    return result.text;
  }

  /**
   * Extract and parse a JSON object from a model response.
   * @param {string} rawContent - Raw model output.
   * @returns {object | null} Parsed JSON object, or null when parsing fails.
   */
  parseJsonObjectResponse(rawContent) {
    const parsed = parseJsonText(rawContent);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  }

  /**
   * Apply exact-match HTML edit operations to a document string.
   * @param {string} documentHTML - HTML content to edit.
   * @param {Array<{type: string, search: string, replace?: string, html?: string, all?: boolean}>} operations - Exact-match operations.
   * @returns {{ html: string, appliedCount: number, failedOperations: Array<object>, appliedOperations: Array<object> }} Patch application result.
   */
  applyDocumentEditOperations(documentHTML, operations) {
    return applyExactDocumentEditOperations(documentHTML, operations);
  }

  /**
   * Restore image and Mermaid aliases inside patch operation strings.
   * @param {Array<{type: string, search: string, replace: string, all: boolean}>} operations - Normalized patch operations.
   * @param {Record<string, object>} imageMap - Image alias map.
   * @param {Record<string, string>} mermaidMap - Mermaid alias map.
   * @returns {Array<{type: string, search: string, replace: string, all: boolean}>} Operations using original document HTML.
   */
  restoreDocumentEditOperationAliases(operations, imageMap, mermaidMap) {
    const restore = (value) => this.restoreAliases(value, imageMap, mermaidMap);
    return operations.map((operation) => ({
      ...operation,
      search: restore(operation.search),
      replace: restore(operation.replace),
    }));
  }

  /**
   * Alias base64 images and Mermaid blocks so the model sees compact placeholders.
   * @param {string} html - Source HTML.
   * @returns {{ html: string, imageMap: Record<string, object>, mermaidMap: Record<string, string> }} Aliased HTML and maps.
   */
  aliasForModel(html) {
    const { aliasedHtml, imageMap } = imageService.aliasImagesInContent(html);
    const afterImages = Object.keys(imageMap || {}).length > 0 ? aliasedHtml : html;
    const { aliasedHtml: aliased, mermaidMap } = aliasMermaidBlocks(afterImages);
    return { html: aliased, imageMap: imageMap || {}, mermaidMap: mermaidMap || {} };
  }

  /**
   * Restore image and Mermaid aliases in a string.
   * @param {string} value - Aliased HTML.
   * @param {Record<string, object>} imageMap - Image alias map.
   * @param {Record<string, string>} mermaidMap - Mermaid alias map.
   * @returns {string} Restored HTML.
   */
  restoreAliases(value, imageMap, mermaidMap) {
    if (typeof value !== 'string' || !value) return value;
    let restored = value;
    if (imageMap && Object.keys(imageMap).length > 0) {
      restored = imageService.restoreImagesFromAliases(restored, imageMap);
    }
    return restoreMermaidAliases(restored, mermaidMap || {});
  }

  /**
   * One find/replace patch-plan attempt.
   * @param {string} documentHTML - Full document HTML.
   * @param {string} editInstruction - Edit request.
   * @param {object} opts - Attempt options.
   * @param {string} opts.task - 'doc.patch' or 'doc.patch.escalate'.
   * @param {number} opts.seed - Seed.
   * @param {string|null} [opts.previousFailure] - Why the previous attempt failed.
   * @param {string} [opts.accountId] - Account id.
   * @param {AbortSignal} [opts.signal] - Abort signal.
   * @returns {Promise<{ result: object|null, failure: string|null }>} Result or failure reason.
   */
  async attemptHtmlPatchPlan(documentHTML, editInstruction, opts) {
    const { html: htmlToSend, imageMap, mermaidMap } = this.aliasForModel(documentHTML);

    const systemInstruction = `You are a precise, conservative HTML document patch planner.
Return a JSON object { "reasoning", "operations" } describing the smallest exact edits.

RULES:
1. Return a compact patch plan, NOT the whole document.
2. Every "search" value must be copied exactly (character for character) from DOCUMENT HTML and should be unique in it.
3. Make only the requested changes; preserve tags and attributes unless the edit explicitly changes them.
4. Prefer one or a few localized operations over broad rewrites.
5. Keep "search" strings as small as possible while still uniquely identifying the edit location.
6. For insert_after and insert_before, put only the new HTML in "replace"; do not repeat the search HTML.
7. Preserve image placeholders ({{IMAGE_n}}) and mermaid placeholders ({{MERMAID_n}}) exactly unless asked to remove them.
8. Never modify elements marked data-tpl-locked="true".`;

    const feedback = opts.previousFailure
      ? `\n\nA previous plan for this edit failed: ${opts.previousFailure}. Copy "search" strings exactly from the HTML below.`
      : '';
    const prompt = `EDIT INSTRUCTION: ${editInstruction}${feedback}

DOCUMENT HTML:
${htmlToSend}`;

    const response = await this.runTask({
      task: opts.task,
      profile: 'patch',
      prompt,
      systemInstruction,
      seed: opts.seed,
      responseSchema: PATCH_PLAN_SCHEMA,
      generationConfig: { maxOutputTokens: 8192 },
      accountId: opts.accountId,
      signal: opts.signal,
    });

    const parsed = response.json;
    const normalized = normalizeDocumentEditOperations(parsed?.operations);
    if (normalized.errors.length > 0 || normalized.operations.length === 0) {
      return { result: null, failure: 'the plan had no valid operations' };
    }

    const { html: patchedHtml, appliedCount, failedOperations, appliedOperations } =
      this.applyDocumentEditOperations(htmlToSend, normalized.operations);

    if (appliedCount === 0 || failedOperations.length > 0 || patchedHtml === htmlToSend) {
      const missing = failedOperations
        .filter((f) => f.reason === 'search_not_found')
        .map((f) => normalized.operations[f.index]?.search)
        .filter(Boolean)
        .slice(0, 3)
        .map((s) => JSON.stringify(String(s).slice(0, 120)));
      return {
        result: null,
        failure: missing.length
          ? `these search strings were not found: ${missing.join(', ')}`
          : 'the operations did not change the document',
      };
    }

    return {
      result: {
        html: this.restoreAliases(patchedHtml, imageMap, mermaidMap),
        thinking: parsed.reasoning ? String(parsed.reasoning).trim() : null,
        editStrategy: 'patch',
        strategy: opts.task === AI_TASKS.DOC_PATCH_ESCALATE ? 'patch-escalated' : 'patch',
        operations: this.restoreDocumentEditOperationAliases(appliedOperations, imageMap, mermaidMap),
        modelUsed: response.model,
      },
      failure: null,
    };
  }

  /**
   * One block-id patch-plan attempt (CONTRACTS §6 ops). Returns `editPlan` for
   * `editor.applyEditPlan`, plus derived `html`/`operations` when every op maps onto the HTML.
   * @param {string} documentHTML - Full document HTML (used to derive html when possible).
   * @param {string} editInstruction - Edit request.
   * @param {Array<{id: string, type?: string, text?: string, html?: string, locked?: boolean}>} blocks - Top-level blocks.
   * @param {object} opts - Same as attemptHtmlPatchPlan.
   * @returns {Promise<{ result: object|null, failure: string|null }>} Result or failure reason.
   */
  async attemptBlockPatchPlan(documentHTML, editInstruction, blocks, opts) {
    const usable = blocks.filter((b) => b && typeof b.id === 'string' && b.id);
    const joined = usable.map((b) => String(b.html ?? '')).join(BLOCK_ALIAS_SEPARATOR);
    const aliased = this.aliasForModel(joined);
    const aliasedParts = aliased.html.split(BLOCK_ALIAS_SEPARATOR);
    const aliasedHtmlFor = aliasedParts.length === usable.length
      ? (index) => aliasedParts[index]
      : (index) => String(usable[index].html ?? '');

    const blockList = usable
      .map((b, index) => {
        const flags = [b.type || 'block', b.level ? `level ${b.level}` : '', b.locked ? 'LOCKED' : '']
          .filter(Boolean)
          .join(', ');
        return `[${b.id}] (${flags})\n${aliasedHtmlFor(index)}`;
      })
      .join('\n\n');

    const systemInstruction = `You are a precise, conservative document editor that plans edits as operations on numbered blocks.
Return a JSON object { "reasoning", "operations" }.

OPERATIONS:
- replace_block {blockId, html}: replace one whole block with new HTML (one or more top-level elements).
- insert_after / insert_before {blockId, html}: add new block HTML next to a block.
- delete_block {blockId}: remove a block.
- replace_text {blockId, find, replace}: change an exact plain-text phrase inside one block (preferred for small wording edits).

RULES:
1. Use only block ids listed in BLOCKS. Never target a block marked LOCKED.
2. Make only the requested changes; keep everything else byte-for-byte.
3. Prefer the smallest operations: replace_text for wording, replace_block for restructuring one block.
4. "find" must be copied exactly from the block's visible text.
5. Preserve image placeholders ({{IMAGE_n}}) and mermaid placeholders ({{MERMAID_n}}) exactly.
6. Do not delete most of the document unless the instruction explicitly asks for it.`;

    const feedback = opts.previousFailure
      ? `\n\nA previous plan for this edit failed: ${opts.previousFailure}.`
      : '';
    const prompt = `EDIT INSTRUCTION: ${editInstruction}${feedback}

BLOCKS:
${blockList}`;

    const response = await this.runTask({
      task: opts.task,
      profile: 'patch',
      prompt,
      systemInstruction,
      seed: opts.seed,
      responseSchema: BLOCK_PLAN_SCHEMA,
      generationConfig: { maxOutputTokens: 8192 },
      accountId: opts.accountId,
      signal: opts.signal,
    });

    const parsed = response.json;
    const rawOps = Array.isArray(parsed?.operations) ? parsed.operations : [];
    if (rawOps.length === 0) {
      return { result: null, failure: 'the plan had no operations' };
    }

    const byId = new Map(usable.map((b) => [b.id, b]));
    const allText = usable.map((b) => b.text ?? htmlToText(b.html)).join('\n');
    const ops = [];
    const problems = [];
    rawOps.forEach((raw, index) => {
      const op = String(raw?.op || '');
      const blockId = typeof raw?.blockId === 'string' && raw.blockId ? raw.blockId : null;
      const block = blockId ? byId.get(blockId) : null;
      if (!BLOCK_EDIT_OPS.includes(op)) {
        problems.push(`operation ${index + 1} has unknown op "${op}"`);
        return;
      }
      if (blockId && !block) {
        problems.push(`operation ${index + 1} targets unknown block ${blockId}`);
        return;
      }
      if (!blockId && op !== 'replace_text') {
        problems.push(`operation ${index + 1} (${op}) has no blockId`);
        return;
      }
      if (block?.locked) {
        problems.push(`operation ${index + 1} targets locked block ${blockId}`);
        return;
      }
      if (op === 'replace_text') {
        const find = typeof raw.find === 'string' ? raw.find : '';
        const scope = block ? (block.text ?? htmlToText(block.html)) : allText;
        if (!find || !scope.includes(find)) {
          problems.push(`operation ${index + 1}: text ${JSON.stringify(find.slice(0, 80))} not found`);
          return;
        }
        ops.push({
          op,
          ...(blockId ? { blockId } : {}),
          find,
          replace: typeof raw.replace === 'string' ? raw.replace : '',
        });
        return;
      }
      if (op !== 'delete_block' && !(typeof raw.html === 'string' && raw.html.trim())) {
        problems.push(`operation ${index + 1} (${op}) has no html`);
        return;
      }
      ops.push({
        op,
        blockId,
        ...(op !== 'delete_block'
          ? { html: this.restoreAliases(raw.html, aliased.imageMap, aliased.mermaidMap) }
          : {}),
      });
    });

    if (problems.length > 0 || ops.length === 0) {
      return { result: null, failure: problems.slice(0, 3).join('; ') || 'no valid operations' };
    }

    // Derive find/replace operations + html when each targeted block's HTML is unique in the document.
    const legacyOps = [];
    let derivable = typeof documentHTML === 'string' && documentHTML.length > 0;
    for (const op of ops) {
      if (!derivable) break;
      const block = op.blockId ? byId.get(op.blockId) : null;
      const blockHtml = block ? String(block.html ?? '') : '';
      if (!block || !blockHtml || countOccurrences(documentHTML, blockHtml) !== 1) {
        derivable = false;
        break;
      }
      if (op.op === 'replace_block') legacyOps.push({ type: 'replace', search: blockHtml, replace: op.html });
      else if (op.op === 'insert_after') legacyOps.push({ type: 'insert_after', search: blockHtml, replace: op.html });
      else if (op.op === 'insert_before') legacyOps.push({ type: 'insert_before', search: blockHtml, replace: op.html });
      else if (op.op === 'delete_block') legacyOps.push({ type: 'delete', search: blockHtml, replace: '' });
      else if (op.op === 'replace_text' && blockHtml.includes(op.find)) {
        legacyOps.push({ type: 'replace', search: blockHtml, replace: blockHtml.split(op.find).join(op.replace) });
      } else {
        derivable = false;
      }
    }

    let html = null;
    let operations;
    if (derivable && legacyOps.length > 0) {
      const applied = this.applyDocumentEditOperations(documentHTML, legacyOps);
      if (applied.failedOperations.length === 0 && applied.appliedCount > 0) {
        html = applied.html;
        operations = applied.appliedOperations;
      }
    }

    return {
      result: {
        html,
        thinking: parsed.reasoning ? String(parsed.reasoning).trim() : null,
        editStrategy: 'block-patch',
        strategy: opts.task === AI_TASKS.DOC_PATCH_ESCALATE ? 'block-patch-escalated' : 'block-patch',
        editPlan: ops,
        ...(operations ? { operations } : {}),
        modelUsed: response.model,
      },
      failure: null,
    };
  }

  /**
   * One patch attempt in block mode (when blocks are given) or find/replace mode.
   * @param {string} documentHTML - Document HTML.
   * @param {string} editInstruction - Edit request.
   * @param {object} opts - Attempt options (task, seed, blocks, previousFailure, accountId, signal).
   * @returns {Promise<{ result: object|null, failure: string|null }>} Attempt outcome.
   */
  async attemptPatchPlan(documentHTML, editInstruction, opts) {
    if (Array.isArray(opts.blocks) && opts.blocks.length > 0) {
      return this.attemptBlockPatchPlan(documentHTML, editInstruction, opts.blocks, opts);
    }
    return this.attemptHtmlPatchPlan(documentHTML, editInstruction, opts);
  }

  /**
   * Edit a document with one fast patch-plan attempt (task 'doc.patch').
   * @param {string} documentHTML - The full document HTML content.
   * @param {string} editInstruction - Natural language description of the edits.
   * @param {object} [options] - Same options as editDocument.
   * @returns {Promise<object | null>} Patch result, or null when the plan could not be applied.
   */
  async editDocumentWithPatchPlan(documentHTML, editInstruction, options = {}) {
    const { result } = await this.attemptPatchPlan(documentHTML, editInstruction, {
      ...options,
      task: AI_TASKS.DOC_PATCH,
      seed: stableSeed(AI_TASKS.DOC_PATCH, options.documentId || '', editInstruction, options.variant ?? 0),
      accountId: this.resolveAccountId(options.accountId),
    });
    return result;
  }

  /**
   * Edit a document from a natural language instruction (models.md P8):
   * 1. patch plan on the fast model ('doc.patch', profile patch, schema, seed);
   * 2. once more with 'doc.patch.escalate' (premium for premium tiers) and failure feedback;
   * 3. full-HTML rewrite ('doc.rewrite') as the last resort.
   *
   * When `options.blocks` is given the plan references block ids and the result carries
   * `editPlan` (ops for `editor.applyEditPlan`); `html`/`operations` are included when they
   * can be derived. Without blocks the classic find/replace operations are used.
   *
   * @param {string} documentHTML - The full document HTML content.
   * @param {string} editInstruction - Natural language description of the edits.
   * @param {object} [options] - Options.
   * @param {Array<{id: string, type?: string, text?: string, html?: string, locked?: boolean}>} [options.blocks] - Top-level blocks with stable ids.
   * @param {string} [options.documentId] - Document id (seed input).
   * @param {number} [options.variant] - Regenerate counter (seed input).
   * @param {string} [options.accountId] - Account for tier resolution.
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @param {boolean} [options.allowRewrite=true] - Allow the full-HTML fallback.
   * @returns {Promise<{ html: string|null, thinking: string|null, editStrategy: string, strategy: string, operations?: Array<object>, editPlan?: Array<object>, modelUsed: string|null }>} Edit result.
   */
  async editDocument(documentHTML, editInstruction, options = {}) {
    const accountId = this.resolveAccountId(options.accountId);
    const variant = options.variant ?? 0;
    const documentId = options.documentId || '';
    const seedFor = (task) => stableSeed(task, documentId, editInstruction, variant);
    let failure = null;

    for (const task of [AI_TASKS.DOC_PATCH, AI_TASKS.DOC_PATCH_ESCALATE]) {
      try {
        const attempt = await this.attemptPatchPlan(documentHTML, editInstruction, {
          task,
          seed: seedFor(task),
          blocks: options.blocks,
          previousFailure: failure,
          accountId,
          signal: options.signal,
        });
        if (attempt.result) return attempt.result;
        failure = attempt.failure;
        console.warn(`editDocument: ${task} plan not applied`, failure);
      } catch (error) {
        if (isAbortError(error)) throw error;
        failure = error?.message || 'the plan request failed';
        console.warn(`editDocument: ${task} failed`, error);
      }
    }

    if (options.allowRewrite === false) {
      throw new AiProxyError(`Could not plan this edit (${failure || 'unknown reason'}).`, { code: 'PATCH_FAILED' });
    }
    return this.rewriteDocumentHtml(documentHTML, editInstruction, {
      accountId,
      signal: options.signal,
      seed: seedFor(AI_TASKS.DOC_REWRITE),
      previousFailure: failure,
    });
  }

  /**
   * Full-HTML rewrite fallback for edits that cannot be expressed as a patch.
   * @param {string} documentHTML - Document HTML.
   * @param {string} editInstruction - Edit request.
   * @param {object} opts - Options (accountId, signal, seed, previousFailure).
   * @returns {Promise<{ html: string, thinking: string|null, editStrategy: string, strategy: string, modelUsed: string|null }>} Result.
   */
  async rewriteDocumentHtml(documentHTML, editInstruction, opts) {
    const { html: htmlToSend, imageMap, mermaidMap } = this.aliasForModel(documentHTML);

    const systemInstruction = `You are a precise, conservative document editor. Make ONLY the changes the user requests and nothing else.
Return a JSON object { "reasoning", "html" } where "html" is the COMPLETE edited document.

RULES:
1. Preserve all HTML formatting, structure, tags and attributes exactly unless the edit changes them.
2. Do not rewrite, rephrase or "improve" anything that was not asked for.
3. Never modify elements marked data-tpl-locked="true"; keep data-tpl-field / data-field-id attributes intact.
4. IMAGES: keep image placeholders such as <img src="{{IMAGE_1}}" /> exactly as-is.
5. MERMAID: keep {{MERMAID_n}} placeholders exactly as-is. For a NEW diagram emit
   <div data-mermaid-block="true" data-mermaid-code="graph TD&#10;  A[Start] --&gt; B[End]"></div>
   with HTML-entity encoding (&amp; &quot; &lt; &gt; and &#10; for newlines) inside data-mermaid-code.`;

    const prompt = `EDIT INSTRUCTION: ${editInstruction}

DOCUMENT HTML:
${htmlToSend}`;

    let parsed = null;
    let rawText = '';
    let modelUsed = null;
    try {
      const response = await this.runTask({
        task: AI_TASKS.DOC_REWRITE,
        profile: 'fill',
        prompt,
        systemInstruction,
        seed: opts.seed,
        responseSchema: HTML_RESULT_SCHEMA,
        generationConfig: { maxOutputTokens: 32768 },
        accountId: opts.accountId,
        signal: opts.signal,
      });
      parsed = response.json;
      rawText = response.text;
      modelUsed = response.model;
    } catch (error) {
      if (error?.code !== 'INVALID_JSON') throw error;
      rawText = String(error.details?.text || '');
      modelUsed = error.model || null;
    }

    const html = typeof parsed?.html === 'string' ? parsed.html.trim() : '';
    if (html) {
      return {
        html: this.restoreAliases(html, imageMap, mermaidMap),
        thinking: parsed.reasoning ? String(parsed.reasoning).trim() : null,
        editStrategy: 'full-html',
        strategy: 'rewrite',
        modelUsed,
      };
    }

    // Unparseable JSON: recover the decoded "html" value. A truncated rewrite would silently drop
    // the end of the document, so it is reported instead of applied.
    const salvaged = salvageHtmlFromJsonReply(rawText);
    if (salvaged?.truncated) {
      throw new AiProxyError(
        'The edited document was too long for one response and was cut off. Try a narrower edit.',
        { code: 'MAX_TOKENS', model: modelUsed, finishReason: 'MAX_TOKENS' }
      );
    }
    if (!salvaged) {
      throw new AiProxyError('The AI could not produce an edited document. Please try again.', {
        code: 'INVALID_JSON',
        model: modelUsed,
      });
    }
    return {
      html: this.restoreAliases(salvaged.html, imageMap, mermaidMap),
      thinking: null,
      editStrategy: 'full-html-fallback',
      strategy: 'rewrite',
      modelUsed,
    };
  }

  /**
   * Generate starter HTML for an empty rich-text document from a natural language brief.
   * @param {string} instruction - What the document should contain (topic, audience, sections).
   * @param {object} [options] - Options.
   * @param {number} [options.variant] - Regenerate counter (seed input).
   * @param {string} [options.accountId] - Account for tier resolution.
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @returns {Promise<{ html: string, thinking: string | null, modelUsed: string | null }>} Generated HTML and optional short rationale.
   */
  async generateInitialDocumentHtml(instruction, options = {}) {
    const systemInstruction = `You are a business writing assistant. The user's document editor is empty. Create an initial HTML draft that gives them a strong starting structure.
Return a JSON object { "reasoning", "html" }.

RULES:
1. "html" is semantic HTML only (<h1>, <h2>, <h3>, <p>, <ul>, <ol>, <li>, <strong>, <table>); no <html>, <head> or <body>.
2. Be substantive: multiple sections and bullet points where appropriate, unless the user asked for minimal content.
3. Match the user's intent (business plan, proposal, project plan, ...). Use [Needs input: ...] markers where facts are unknown instead of inventing them.
4. Professional, neutral tone unless the user asked otherwise.
5. MERMAID: for a requested diagram emit <div data-mermaid-block="true" data-mermaid-code="graph TD&#10;  A[Start] --&gt; B[End]"></div> with HTML-entity encoding inside data-mermaid-code.`;

    const prompt = `USER REQUEST:\n${instruction}`;
    let parsed = null;
    let rawText = '';
    let modelUsed = null;
    try {
      const response = await this.runTask({
        task: AI_TASKS.DOC_REWRITE,
        profile: 'fill',
        prompt,
        systemInstruction,
        seed: stableSeed('doc.draft', instruction, options.variant ?? 0),
        responseSchema: HTML_RESULT_SCHEMA,
        generationConfig: { maxOutputTokens: 16384 },
        accountId: this.resolveAccountId(options.accountId),
        signal: options.signal,
      });
      parsed = response.json;
      rawText = response.text;
      modelUsed = response.model;
    } catch (error) {
      if (error?.code !== 'INVALID_JSON') throw error;
      rawText = String(error.details?.text || '');
      modelUsed = error.model || null;
    }

    const html = typeof parsed?.html === 'string' ? parsed.html.trim() : '';
    if (html) {
      return {
        html,
        thinking: parsed.reasoning ? String(parsed.reasoning).trim() : null,
        modelUsed,
      };
    }
    // Unparseable JSON: recover the decoded "html" value. A draft cut off at the output limit is
    // still a usable starting point, so it is kept up to its last closing tag.
    const salvaged = salvageHtmlFromJsonReply(rawText);
    let fallback = salvaged?.html || '';
    if (salvaged?.truncated) {
      const closers = [...fallback.matchAll(/<\/[a-z][a-z0-9]*\s*>/gi)];
      const last = closers[closers.length - 1];
      fallback = last ? fallback.slice(0, last.index + last[0].length).trim() : '';
    }
    if (!fallback.includes('<')) {
      throw new AiProxyError('Could not generate valid HTML for an empty document.', {
        code: salvaged?.truncated ? 'MAX_TOKENS' : 'INVALID_JSON',
        model: modelUsed,
      });
    }
    return { html: fallback, thinking: null, modelUsed };
  }

  // Get available extraction types (canonical list; shared with Edge → Cloud Run extraction)
  getExtractionTypes() {
    return getExtractionTypeOptions();
  }

  /**
   * Analyze a gold document's structure for template generation (task 'template.analyze',
   * profile 'extract', JSON schema, stable seed: the same file yields the same analysis).
   * @param {string | {text: string, html?: string, fileType?: string}} fileContent - Document text or Mammoth output.
   * @param {string} [fileName] - Original file name.
   * @param {function(object): void} [onLargeDocumentDetected] - Called before chunked processing.
   * @param {object} [options] - Options.
   * @param {string} [options.accountId] - Account for tier resolution.
   * @param {number} [options.variant] - Regenerate counter (seed input).
   * @param {AbortSignal} [options.signal] - Abort signal.
   * @returns {Promise<object>} Validated analysis `{ documentTitle, structure, metadata }`.
   */
  async analyzeDocumentStructure(fileContent, fileName = '', onLargeDocumentDetected = null, options = {}) {
    // Handle both legacy string format and new structured format
    const isStructuredContent = typeof fileContent === 'object' && fileContent.text;
    const contentText = isStructuredContent ? fileContent.text : fileContent;
    const htmlContent = isStructuredContent ? fileContent.html : null;
    const fileType = isStructuredContent ? fileContent.fileType : 'text';
    const accountId = this.resolveAccountId(options.accountId);
    const variant = options.variant ?? 0;

    // Check if document is large and needs chunking
    const isLargeDocument = contentText.length > 100000 || (htmlContent && htmlContent.length > 200000);

    if (isLargeDocument && htmlContent) {
      if (onLargeDocumentDetected) {
        onLargeDocumentDetected({
          type: 'analysis',
          contentLength: contentText.length,
          htmlLength: htmlContent.length,
          isLargeDocument: true
        });
      }
      return await this.analyzeDocumentStructureChunked(fileContent, fileName, { accountId, variant, signal: options.signal });
    }

    const systemInstruction = `You are an expert at turning a finished "gold standard" document into a reusable document TEMPLATE.

PRIMARY GOAL — TEMPLATIZATION:
Identify what must become VARIABLE when a user later generates a new document from this template. At generation time the app passes NEW context: project name and description, requirements (title + description), meeting summaries, tone — not this gold document's client names, numbers or one-off details.

For each section output, in source order:
- Headings: stable section titles (structure only); suggestedPrompt must be "".
- Content blocks: "content" = a short grounded excerpt from the source (roughly 200-600 characters) so authors see what the slot represents; "suggestedPrompt" = a SHORT templatization hint describing what this block must CONVEY for a new project (domain-specific, e.g. pricing line items vs narrative intro). For tables, name the columns. No meta "Generate/Create/Draft/Write…" phrasing.

VALUE CONSTRAINTS:
- htmlTag: h1, h2, h3, p, ul, ol or table. type: "heading" or "content".
- contentType: introduction, overview, detailed, list, table or conclusion (headings use "overview").
- Choose htmlTag by structure: table for tabular data, ul/ol for lists, p for prose.
- Table column titles in the gold document are preserved in the saved template; suggestedPrompt still describes what the table conveys for a new project.

${TEMPLATE_FIELD_RULES}`;

    // Optimize content for context size and structure analysis
    let analysisContent = contentText;
    let analysisPrompt = '';

    if (htmlContent && fileType === 'word') {
      const maxContentLength = TEMPLATE_ANALYSIS_TEXT_MAX;
      if (contentText.length > maxContentLength) {
        analysisContent = contentText.substring(0, maxContentLength) + '\n\n[Content truncated for analysis...]';
      }

      analysisPrompt = `Document to analyze (Word document converted to HTML):
${fileName ? `File: ${fileName}\n` : ''}
Content Length: ${contentText.length} characters${contentText.length > maxContentLength ? ' (truncated for analysis)' : ''}

HTML Structure (for hierarchy analysis):
${htmlContent}

Text Content:
${analysisContent}

ANALYSIS INSTRUCTIONS:
- Use the HTML structure to identify document hierarchy (h1, h2, h3, etc.)
- Decide which blocks are templatized (variable per project) vs fixed structure
- For each content block, include a grounded excerpt in "content" and a short templatization hint in "suggestedPrompt"`;
    } else {
      const maxContentLength = 10000;
      if (contentText.length > maxContentLength) {
        analysisContent = contentText.substring(0, maxContentLength) + '\n\n[Content truncated for analysis...]';
      }

      analysisPrompt = `Document to analyze (${analysisContent.length} characters):
${fileName ? `File: ${fileName}\n` : ''}
Content:
${analysisContent}`;
    }

    const seed = stableSeed(
      AI_TASKS.TEMPLATE_ANALYZE,
      fnv1a32Hex(`${contentText}\u0000${htmlContent || ''}`),
      variant
    );

    /**
     * Run the analysis with a prompt and output budget.
     * @param {string} prompt - Prompt.
     * @param {number} maxOutputTokens - Output budget.
     * @returns {Promise<object>} Parsed analysis JSON.
     */
    const analyze = async (prompt, maxOutputTokens) => {
      const response = await this.runTask({
        task: AI_TASKS.TEMPLATE_ANALYZE,
        profile: 'extract',
        prompt,
        systemInstruction,
        seed,
        responseSchema: TEMPLATE_ANALYSIS_SCHEMA,
        generationConfig: { maxOutputTokens },
        accountId,
        signal: options.signal,
      });
      return response.json;
    };

    try {
      const analysis = await analyze(
        `${analysisPrompt}\n\nReturn the templatization framework: headings and content slots in document order.`,
        32768
      );
      return this.validateDocumentAnalysis(analysis, fileContent);
    } catch (error) {
      if (isAbortError(error)) throw error;
      console.error('Error analyzing document structure:', error);

      if (error?.code === 'MAX_TOKENS' || /MAX_TOKENS|too long/i.test(error?.message || '')) {
        console.warn('Analysis hit the output limit; retrying with a concise prompt...');
        try {
          const analysis = await analyze(
            `${analysisPrompt}\n\nProvide a CONCISE structural analysis: main sections only, short excerpts (under 200 characters) and short hints.`,
            16384
          );
          return this.validateDocumentAnalysis(analysis, fileContent);
        } catch (retryError) {
          if (isAbortError(retryError)) throw retryError;
          console.error('Concise retry also failed, using fallback analysis:', retryError);
          return this.createFallbackAnalysis(fileContent, fileName);
        }
      }

      if (
        error?.code === 'INVALID_JSON' ||
        error?.code === 'RATE_LIMITED' ||
        /quota|rate limit|429|Too Many Requests/i.test(error?.message || '')
      ) {
        console.warn('Using offline analysis:', error?.code || error?.message);
        return this.createFallbackAnalysis(fileContent, fileName);
      }

      throw error;
    }
  }

  /**
   * Create a field-id allocator for one template build. Ids follow CONTRACTS §5:
   * 'f_' + 8 hex chars of FNV-1a(ordinalIndex + ':' + instruction), ordinal in document order.
   * @returns {{ next: function(string): string, count: function(): number }} Allocator.
   */
  createFieldIdAllocator() {
    let ordinal = 0;
    const used = new Set();
    return {
      next: (instruction) => {
        let id = `f_${fnv1a32Hex(`${ordinal}:${instruction}`)}`;
        let salt = 1;
        while (used.has(id)) {
          id = `f_${fnv1a32Hex(`${ordinal}:${instruction}:${salt}`)}`;
          salt += 1;
        }
        used.add(id);
        ordinal += 1;
        return id;
      },
      count: () => ordinal,
    };
  }

  /**
   * Serialize a template field (CONTRACTS §5). The instruction is saved in its legacy brace
   * storage form (`{instruction}`, `{?instruction}` when optional; wrapFieldInstruction), so the
   * previously deployed worker, which only fills `{...}` text, still fills generated templates;
   * the field parser strips the wrapper again.
   * @param {object} field - Field description.
   * @param {string} field.instruction - Instruction shown to authors and the generator.
   * @param {'rich'|'list'|'table_rows'|'text'} field.kind - Field kind.
   * @param {boolean} [field.required=true] - Whether the field must be filled.
   * @param {'requirements'|'sessions'|'project'|'any'} [field.source='any'] - Evidence source.
   * @param {number} [field.maxWords] - Length limit.
   * @param {boolean} [field.inline] - Render as <span> (inline) instead of <div> (block).
   * @param {{ next: function(string): string }} ids - Field id allocator.
   * @returns {string} Field HTML.
   */
  buildTemplateFieldHtml(field, ids) {
    const instruction = truncateTemplatizationHint(field.instruction) || 'Content for this section from the project context';
    const tag = field.inline ? 'span' : 'div';
    const kind = ['rich', 'list', 'table_rows', 'text'].includes(field.kind) ? field.kind : (field.inline ? 'text' : 'rich');
    const source = ['requirements', 'sessions', 'project', 'any'].includes(field.source) ? field.source : 'any';
    const required = field.required === false ? 'false' : 'true';
    const maxWords = Number.isInteger(field.maxWords) && field.maxWords > 0 ? field.maxWords : null;
    const id = ids.next(instruction);
    return (
      `<${tag} data-tpl-field="${id}" data-kind="${kind}" data-required="${required}" data-source="${source}"` +
      `${maxWords ? ` data-max-words="${maxWords}"` : ''}>${escapeHtml(wrapFieldInstruction(instruction, required !== 'false'))}</${tag}>`
    );
  }

  /**
   * Word budget for a content slot: the model's maxWords when sane, else derived from the
   * gold excerpt length.
   * @param {object} section - Analysis section.
   * @param {number} fallback - Default when nothing is known.
   * @returns {number} Max words.
   */
  estimateFieldMaxWords(section, fallback) {
    if (Number.isInteger(section?.maxWords) && section.maxWords >= 10 && section.maxWords <= 2000) {
      return section.maxWords;
    }
    const words = String(section?.content || '').split(/\s+/).filter(Boolean).length;
    if (words < 10) return fallback;
    return Math.min(600, Math.max(40, Math.round((words * 1.5) / 10) * 10));
  }

  /**
   * Turn a gold-document table into a template table: header rows stay fixed text; data rows
   * become either one requirement repeat row (`<tr data-tpl-repeat="requirements">` with
   * {{req.*}} variables) when the columns describe requirements, or a single `table_rows`
   * field spanning the table.
   * @param {string} tableOuterHtml - Serialized table from Mammoth HTML.
   * @param {string} sectionHint - Section-level templatization hint.
   * @param {{ next: function(string): string }} ids - Field id allocator.
   * @param {object} [section] - Analysis section (required/source/maxWords hints).
   * @returns {string} Template table HTML.
   */
  templatizePreservedTableHtml(tableOuterHtml, sectionHint, ids, section = {}) {
    const hint = sectionHint || 'Table rows from project context';
    const fallbackTable = () =>
      `<table><tbody><tr><td>${this.buildTemplateFieldHtml({
        instruction: hint,
        kind: 'table_rows',
        required: section.required,
        source: section.source,
      }, ids)}</td></tr></tbody></table>`;

    if (!tableOuterHtml || typeof DOMParser === 'undefined') return fallbackTable();

    const doc = new DOMParser().parseFromString(`<div id="quill-tpl-table-root">${tableOuterHtml}</div>`, 'text/html');
    const table = doc.getElementById('quill-tpl-table-root')?.querySelector('table');
    if (!table) return fallbackTable();

    const rows = Array.from(table.querySelectorAll('tr'));
    let headerRows = [];
    if (table.querySelector('thead')) {
      headerRows = Array.from(table.querySelectorAll('thead tr'));
    } else if (rows.length > 1) {
      headerRows = [rows[0]];
    }
    const headerTexts = headerRows.length
      ? Array.from(headerRows[headerRows.length - 1].querySelectorAll('th, td')).map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim())
      : [];
    const columnCount = Math.max(
      headerTexts.length,
      ...rows.map((r) => r.querySelectorAll('th, td').length),
      1
    );

    const headerHtml = headerRows.length
      ? `<thead>${headerRows
          .map((tr) => `<tr>${Array.from(tr.querySelectorAll('th, td'))
            .map((c) => `<th>${escapeHtml((c.textContent || '').replace(/\s+/g, ' ').trim())}</th>`)
            .join('')}</tr>`)
          .join('')}</thead>`
      : '';

    /** @type {Array<string|null>} */
    const requirementColumns = headerTexts.map((label) => {
      const l = label.toLowerCase();
      if (/^(req\.?|req(uirement)?\.?\s*(id|#|no\.?|number)|id|#|ref(erence)?(\s*(id|#|no\.?))?)$/.test(l)) {
        return '{{req.id}}';
      }
      if (/priority/.test(l)) return '{{req.priority}}';
      if (/(description|details?|acceptance)/.test(l)) return '{{req.description}}';
      if (/(requirement|user stor|story|feature|title|name)/.test(l)) return '{{req.title}}';
      return null;
    });
    const mapped = requirementColumns.filter(Boolean);
    const looksLikeRequirements =
      mapped.length >= 2 && (mapped.includes('{{req.title}}') || mapped.includes('{{req.description}}'));

    let bodyHtml;
    if (looksLikeRequirements) {
      const cells = headerTexts.map((label, index) => {
        const variable = requirementColumns[index];
        if (variable) return `<td>${variable}</td>`;
        return `<td>${this.buildTemplateFieldHtml({
          instruction: `${label || `Column ${index + 1}`} for this requirement`,
          kind: 'text',
          inline: true,
          source: 'requirements',
          maxWords: 40,
        }, ids)}</td>`;
      });
      bodyHtml = `<tbody><tr data-tpl-repeat="requirements">${cells.join('')}</tr></tbody>`;
    } else {
      const columns = headerTexts.filter(Boolean);
      const instruction = columns.length
        ? `${hint} — one row per item with columns: ${columns.join(' | ')}`
        : hint;
      bodyHtml = `<tbody><tr><td colspan="${columnCount}">${this.buildTemplateFieldHtml({
        instruction,
        kind: 'table_rows',
        required: section.required,
        source: section.source,
        maxWords: this.estimateFieldMaxWords(section, 250),
      }, ids)}</td></tr></tbody>`;
    }

    return `<table>${headerHtml}${bodyHtml}</table>`;
  }

  /**
   * Find the first &lt;table&gt; between a heading and the next heading (same traversal as block hints).
   * @param {Element} currentHeading - Heading element.
   * @param {Element | null} nextHeading - Next heading or null.
   * @returns {HTMLTableElement | null} Table element or null.
   */
  getFirstTableElementBetweenHeadings(currentHeading, nextHeading) {
    let el = currentHeading.nextElementSibling;
    while (el && el !== nextHeading) {
      const tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'table') return el;
      const nested = el.querySelector && el.querySelector('table');
      if (nested) return nested;
      el = el.nextElementSibling;
    }
    return null;
  }

  /**
   * Collect verbatim &lt;p&gt; blocks that appear before the first table (e.g. intro line before a challenge matrix).
   * @param {Element} currentHeading - Heading element.
   * @param {Element | null} nextHeading - Next heading or null.
   * @returns {string} Concatenated HTML or empty string.
   */
  getParagraphHtmlBeforeFirstTable(currentHeading, nextHeading) {
    const parts = [];
    let el = currentHeading.nextElementSibling;
    while (el && el !== nextHeading) {
      const tag = el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'table') break;
      if (tag === 'p' && (el.textContent || '').trim().length > 0) {
        parts.push(el.outerHTML);
      }
      el = el.nextElementSibling;
    }
    return parts.join('\n');
  }

  /**
   * Attach serialized table HTML from the Word conversion for sections marked as tables.
   * @param {object[]} structure - Structure array.
   * @param {string | object} originalContent - `{ html }` from Mammoth.
   * @returns {object[]} Sections possibly with `preservedTableOuterHtml`.
   */
  attachPreservedTableFragments(structure, originalContent) {
    const html = typeof originalContent === 'object' && originalContent?.html
      ? originalContent.html
      : null;
    if (!html || typeof DOMParser === 'undefined' || !Array.isArray(structure)) {
      return structure;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

    let lastHeadingIndex = -1;
    return structure.map((section) => {
      if (section.type === 'heading') {
        lastHeadingIndex += 1;
        return section;
      }
      if (
        section.type === 'content' &&
        section.htmlTag === 'table' &&
        lastHeadingIndex >= 0 &&
        lastHeadingIndex < headings.length
      ) {
        const h = headings[lastHeadingIndex];
        const nextH = headings[lastHeadingIndex + 1] || null;
        const tableEl = this.getFirstTableElementBetweenHeadings(h, nextH);
        if (tableEl) {
          const leadInHtml = this.getParagraphHtmlBeforeFirstTable(h, nextH);
          return {
            ...section,
            preservedTableOuterHtml: tableEl.outerHTML,
            ...(leadInHtml ? { preservedLeadInHtml: leadInHtml } : {})
          };
        }
      }
      return section;
    });
  }

  /**
   * Build template HTML from an analysis using the shared field format (CONTRACTS §5):
   * headings stay fixed (escaped) text, the document title becomes {{doc.title}}, and every
   * content slot becomes a `data-tpl-field` block with kind/required/source/max-words. Output
   * is a pure function of the analysis, so the same analysis always yields the same template.
   * @param {object} analysis - Validated analysis.
   * @param {string} [templateName] - Template name.
   * @param {string} [templateDescription] - Template description.
   * @returns {Promise<{name: string, description: string, content: string, metadata: object}>} Template draft.
   */
  async generateTemplateFromAnalysis(analysis, templateName = '', templateDescription = '') {
    try {
      const ids = this.createFieldIdAllocator();
      const structure = Array.isArray(analysis?.structure) ? analysis.structure : [];
      const parts = [];
      const normalizedTitle = String(analysis?.documentTitle || '').replace(/\s+/g, ' ').trim().toLowerCase();
      let titleEmitted = false;

      if (normalizedTitle) {
        const firstHeading = structure.find((s) => s.type === 'heading' || ['h1', 'h2', 'h3'].includes(s.htmlTag));
        const firstIsTitle =
          firstHeading && String(firstHeading.title || '').replace(/\s+/g, ' ').trim().toLowerCase() === normalizedTitle;
        if (!firstIsTitle) {
          parts.push('<h1>{{doc.title}}</h1>');
          titleEmitted = true;
        }
      }

      for (const section of structure) {
        const isHeadingSection = section.type === 'heading' || ['h1', 'h2', 'h3'].includes(section.htmlTag);

        if (isHeadingSection) {
          if (!section.title) continue;
          const headingTag = ['h1', 'h2', 'h3'].includes(section.htmlTag) ? section.htmlTag : 'h2';
          const headingText = String(section.title).replace(/\s+/g, ' ').trim();
          if (!titleEmitted && normalizedTitle && headingText.toLowerCase() === normalizedTitle) {
            parts.push(`<${headingTag}>{{doc.title}}</${headingTag}>`);
            titleEmitted = true;
          } else {
            parts.push(`<${headingTag}>${escapeHtml(headingText)}</${headingTag}>`);
          }
          continue;
        }

        const instruction = section.suggestedPrompt || this.createGenericPrompt(section.contentType, section.title || 'content');
        const tag = section.htmlTag || 'p';
        const common = { required: section.required, source: section.source };

        if (tag === 'ul' || tag === 'ol' || section.fieldKind === 'list') {
          parts.push(this.buildTemplateFieldHtml({
            ...common,
            instruction,
            kind: 'list',
            maxWords: this.estimateFieldMaxWords(section, 150),
          }, ids));
        } else if (tag === 'table') {
          if (section.preservedTableOuterHtml) {
            if (section.preservedLeadInHtml) parts.push(section.preservedLeadInHtml);
            parts.push(this.templatizePreservedTableHtml(section.preservedTableOuterHtml, instruction, ids, section));
          } else {
            parts.push(this.buildTemplateFieldHtml({
              ...common,
              instruction: `${instruction} Present it as a table.`,
              kind: 'rich',
              maxWords: this.estimateFieldMaxWords(section, 250),
            }, ids));
          }
        } else {
          const kind = section.fieldKind === 'text' ? 'text' : 'rich';
          parts.push(this.buildTemplateFieldHtml({
            ...common,
            instruction,
            kind,
            maxWords: this.estimateFieldMaxWords(section, kind === 'text' ? 40 : 200),
          }, ids));
        }
      }

      return {
        name: templateName || analysis.documentTitle || 'Auto-Generated Template',
        description: templateDescription || `Template generated from ${analysis.metadata?.documentType || 'document'} analysis`,
        content: parts.join('\n'),
        metadata: {
          generatedFrom: 'document-analysis',
          fieldFormat: 'data-tpl-field',
          fieldCount: ids.count(),
          sourceAnalysis: analysis,
          createdAt: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error generating template from analysis:', error);
      throw new Error('Failed to generate template from document analysis');
    }
  }

  /**
   * First substantive block tag between a heading and the next heading (Word HTML).
   * @param {Element} currentHeading - Heading element.
   * @param {Element | null} nextHeading - Next heading or null.
   * @returns {'p' | 'ul' | 'ol' | 'table'} Block tag hint.
   */
  getFirstBlockTagBetweenHeadings(currentHeading, nextHeading) {
    const siblings = [];
    let el = currentHeading.nextElementSibling;
    while (el && el !== nextHeading) {
      siblings.push(el);
      el = el.nextElementSibling;
    }
    // Prefer table / lists over an intro paragraph (common: &lt;p&gt; then &lt;table&gt;)
    for (const node of siblings) {
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (tag === 'table') return 'table';
      if (tag === 'ul') return 'ul';
      if (tag === 'ol') return 'ol';
      const nested = node.querySelector && node.querySelector('table, ul, ol');
      if (nested) {
        const nt = nested.tagName.toLowerCase();
        if (nt === 'table' || nt === 'ul' || nt === 'ol') return nt;
      }
    }
    for (const node of siblings) {
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (tag === 'p' && (node.textContent || '').trim().length > 0) return 'p';
    }
    return 'p';
  }

  /**
   * Align content `htmlTag` with the first block element in the source HTML when the model returned `p`.
   * @param {object[]} structure - Validated structure array.
   * @param {string | object} originalContent - Original file content or `{ html }`.
   * @returns {object[]} Structure with upgraded tags when appropriate.
   */
  mergeDomBlockHintsIntoStructure(structure, originalContent) {
    const html = typeof originalContent === 'object' && originalContent?.html
      ? originalContent.html
      : null;
    if (!html || typeof DOMParser === 'undefined' || !Array.isArray(structure)) {
      return structure;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const hints = [];
    headings.forEach((heading, idx) => {
      const next = headings[idx + 1] || null;
      hints.push(this.getFirstBlockTagBetweenHeadings(heading, next));
    });

    let currentHeadingIndex = -1;
    return structure.map((section) => {
      if (section.type === 'heading') {
        currentHeadingIndex += 1;
        return section;
      }
      if (
        section.type === 'content' &&
        currentHeadingIndex >= 0 &&
        currentHeadingIndex < hints.length
      ) {
        const hint = hints[currentHeadingIndex];
        if (hint === 'table' && section.htmlTag === 'p') {
          return {
            ...section,
            htmlTag: 'table',
            contentType: section.contentType === 'detailed' ? 'table' : section.contentType
          };
        }
        if (hint === 'ul' && section.htmlTag === 'p') {
          return {
            ...section,
            htmlTag: 'ul',
            contentType: section.contentType === 'detailed' ? 'list' : section.contentType
          };
        }
        if (hint === 'ol' && section.htmlTag === 'p') {
          return {
            ...section,
            htmlTag: 'ol',
            contentType: section.contentType === 'detailed' ? 'list' : section.contentType
          };
        }
      }
      return section;
    });
  }

  // Validate and enhance document analysis
  validateDocumentAnalysis(analysis, originalContent) {
    // Define valid options that match the frontend select components
    const validHtmlTags = ['h1', 'h2', 'h3', 'p', 'ul', 'ol', 'table'];
    const validContentTypes = ['introduction', 'overview', 'detailed', 'list', 'table', 'conclusion'];
    
    // Helper function to map invalid HTML tags to valid ones
    const mapHtmlTag = (tag, type) => {
      if (validHtmlTags.includes(tag)) {
        return tag;
      }
      
      // Map invalid tags to valid ones
      const tagMap = {
        'pre': 'p',
        'blockquote': 'p',
        'div': 'p',
        'span': 'p',
        'h4': 'h3',
        'h5': 'h3',
        'h6': 'h3'
      };
      
      // If it's a heading type, prefer h2
      if (type === 'heading') {
        return 'h2';
      }
      
      return tagMap[tag] || 'p';
    };
    
    // Helper function to map invalid content types to valid ones
    const mapContentType = (contentType, title = '') => {
      if (validContentTypes.includes(contentType)) {
        return contentType;
      }
      
      // Map invalid content types to valid ones
      const typeMap = {
        'heading': 'overview', // Headings become overview content
        'paragraph': 'detailed',
        'code': 'detailed',
        'quote': 'detailed',
        'summary': 'conclusion',
        'abstract': 'introduction'
      };
      
      // Smart mapping based on title keywords
      const titleLower = title.toLowerCase();
      if (titleLower.includes('introduction') || titleLower.includes('background')) {
        return 'introduction';
      }
      if (titleLower.includes('overview') || titleLower.includes('summary') || titleLower.includes('scope')) {
        return 'overview';
      }
      if (titleLower.includes('conclusion') || titleLower.includes('next steps') || titleLower.includes('recommendation')) {
        return 'conclusion';
      }
      if (titleLower.includes('list') || titleLower.includes('requirements') || titleLower.includes('features')) {
        return 'list';
      }
      if (titleLower.includes('table') || titleLower.includes('comparison') || titleLower.includes('specification')) {
        return 'table';
      }
      
      return typeMap[contentType] || 'detailed';
    };

    // Ensure required fields exist
    const validated = {
      documentTitle: analysis.documentTitle || 'Document Template',
      structure: Array.isArray(analysis.structure) ? analysis.structure : [],
      metadata: analysis.metadata || {}
    };

    // Ensure each structure element has required fields and valid values
    validated.structure = validated.structure.map((section, index) => {
      const sectionType = section.type || 'content';
      const originalHtmlTag = section.htmlTag || (section.type === 'heading' ? 'h2' : 'p');
      const originalContentType = section.contentType || 'detailed';
      const normalizedContentType = mapContentType(originalContentType, section.title);
      
      let rawPrompt = sectionType === 'heading'
        ? ''
        : (section.suggestedPrompt || this.createGenericPrompt(normalizedContentType, section.title));

      if (sectionType !== 'heading') {
        rawPrompt = truncateTemplatizationHint(rawPrompt);
      }

      const normalized = {
        level: Math.min(Math.max(section.level || 1, 1), 3), // Ensure level is between 1-3
        type: sectionType,
        title: section.title || `Section ${index + 1}`,
        htmlTag: mapHtmlTag(originalHtmlTag, sectionType),
        content: section.content || '',
        contentType: normalizedContentType,
        suggestedPrompt: rawPrompt,
        position: section.position || index + 1
      };
      if (sectionType !== 'heading') {
        if (['rich', 'list', 'table_rows', 'text'].includes(section.fieldKind)) normalized.fieldKind = section.fieldKind;
        if (typeof section.required === 'boolean') normalized.required = section.required;
        if (['requirements', 'sessions', 'project', 'any'].includes(section.source)) normalized.source = section.source;
        if (Number.isInteger(section.maxWords) && section.maxWords > 0) normalized.maxWords = section.maxWords;
      }
      return normalized;
    });

    validated.structure = this.mergeDomBlockHintsIntoStructure(validated.structure, originalContent);
    validated.structure = this.attachPreservedTableFragments(validated.structure, originalContent);

    // Enhanced metadata
    validated.metadata = {
      totalSections: validated.structure.length,
      complexity: validated.metadata.complexity || 'medium',
      documentType: validated.metadata.documentType || 'document',
      recommendedTemplateStructure: validated.metadata.recommendedTemplateStructure || 'Standard document template with headers and content sections',
      originalLength: typeof originalContent === 'string' ? originalContent.length : originalContent.text?.length || 0,
      validationApplied: true, // Flag to indicate validation was applied
      ...validated.metadata
    };

    return validated;
  }

  // Create fallback analysis when AI parsing fails
  createFallbackAnalysis(fileContent, fileName) {
    // Handle both legacy string format and new structured format
    const isStructuredContent = typeof fileContent === 'object' && fileContent.text;
    const contentText = isStructuredContent ? fileContent.text : fileContent;
    const htmlContent = isStructuredContent ? fileContent.html : null;
    const fileType = isStructuredContent ? fileContent.fileType : 'text';
    
    // For Word documents with HTML, try to extract structure from HTML first
    if (htmlContent && fileType === 'word') {
      try {
        return this.createHtmlBasedAnalysis(htmlContent, contentText, fileName);
      } catch (error) {
        console.warn('Failed to analyze HTML structure, falling back to text analysis:', error);
      }
    }
    
    // Check if this looks like binary data (common in legacy .docx files)
    const isBinaryData = this.detectBinaryContent(contentText);
    if (isBinaryData) {
      return this.createBinaryFileAnalysis(fileName);
    }

    const lines = contentText.split('\n').filter(line => line.trim());
    const structure = [];
    let position = 1;
    let currentSection = null;
    let contentBuffer = [];

    // Process lines to identify headers and group content
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (trimmed.length === 0) return;

      // Enhanced header detection
      const isHeader = this.isLikelyHeader(trimmed, lines, index);

      if (isHeader) {
        // Save previous section's content if exists
        if (currentSection && contentBuffer.length > 0) {
          const contentText = contentBuffer.join(' ');
          const contentType = this.analyzeContentTypeForFallback(contentText);
          
          structure.push({
            level: 2,
            type: 'content',
            title: `${currentSection.title} Content`,
            htmlTag: 'p', // Always use paragraph for content
            content: contentText.substring(0, 200) + (contentText.length > 200 ? '...' : ''),
            contentType: contentType,
            suggestedPrompt: this.createGenericPrompt(contentType, currentSection.title),
            position: position++
          });
          contentBuffer = [];
        }

        // Create new header section
        currentSection = {
          level: 1,
          type: 'heading',
          title: trimmed,
          htmlTag: 'h2', // Use valid HTML tag
          content: trimmed,
          contentType: 'overview', // Use valid content type for headings
          suggestedPrompt: '',
          position: position++
        };
        structure.push(currentSection);
      } else {
        // Accumulate content for current section
        if (trimmed.length > 20) { // Only meaningful content
          contentBuffer.push(trimmed);
        }
      }
    });

    // Handle final section content
    if (currentSection && contentBuffer.length > 0) {
      const contentText = contentBuffer.join(' ');
      const contentType = this.analyzeContentTypeForFallback(contentText);
      
      structure.push({
        level: 2,
        type: 'content',
        title: `${currentSection.title} Content`,
        htmlTag: 'p', // Always use paragraph for content
        content: contentText.substring(0, 200) + (contentText.length > 200 ? '...' : ''),
        contentType: contentType,
        suggestedPrompt: this.createGenericPrompt(contentType, currentSection.title),
        position: position++
      });
    }

    return {
      documentTitle: fileName ? fileName.replace(/\.[^/.]+$/, '') : 'Document Template',
      structure: structure,
      metadata: {
        totalSections: structure.length,
        complexity: 'medium',
        documentType: 'document',
        recommendedTemplateStructure: 'Basic document template with identified headers and content sections',
        fallbackGenerated: true
      }
    };
  }

  // Analyze content to determine its type
  analyzeContentType(content) {
    if (!content || content.length < 10) return 'detailed';
    
    const text = content.toLowerCase();
    
    // Check for introduction patterns
    if (text.includes('introduction') || text.includes('overview') || 
        text.includes('summary') || text.includes('background') ||
        text.match(/^(this document|this project|this report)/)) {
      return 'introduction';
    }
    
    // Check for overview patterns
    if (text.includes('scope') || text.includes('objectives') || 
        text.includes('goals') || text.includes('purpose') ||
        text.match(/provides? an? (overview|summary)/)) {
      return 'overview';
    }
    
    // Check for list patterns
    if ((text.match(/\n\s*[-•*]\s/g) || []).length >= 2 || // Multiple bullet points
        (text.match(/\n\s*\d+\.\s/g) || []).length >= 2 || // Multiple numbered items
        text.includes('requirements:') || text.includes('features:') ||
        text.includes('includes:') || text.includes('contains:')) {
      return 'list';
    }
    
    // Check for table patterns
    if (text.includes('|') || text.includes('\t') ||
        text.match(/\b(table|column|row|header)\b/) ||
        text.includes('comparison') || text.includes('specification')) {
      return 'table';
    }
    
    // Check for conclusion patterns
    if (text.includes('conclusion') || text.includes('summary') ||
        text.includes('next steps') || text.includes('recommendations') ||
        text.includes('in summary') || text.includes('to conclude')) {
      return 'conclusion';
    }
    
    // Default to detailed content (valid option)
    return 'detailed';
  }

  // Infer document type from content
  inferDocumentType(content) {
    if (!content) return 'document';
    
    const text = content.toLowerCase();
    
    if (text.includes('requirements') || text.includes('specification') || 
        text.includes('functional') || text.includes('user stories')) {
      return 'requirements';
    }
    
    if (text.includes('proposal') || text.includes('recommendation') || 
        text.includes('solution') || text.includes('approach')) {
      return 'proposal';
    }
    
    if (text.includes('manual') || text.includes('guide') || 
        text.includes('instructions') || text.includes('how to')) {
      return 'manual';
    }
    
    if (text.includes('report') || text.includes('analysis') || 
        text.includes('findings') || text.includes('results')) {
      return 'report';
    }
    
    if (text.includes('design') || text.includes('architecture') || 
        text.includes('technical') || text.includes('implementation')) {
      return 'specification';
    }
    
    return 'document';
  }

  // Create generic prompts based on content type
  createGenericPrompt(contentType, sectionTitle = '') {
    const label = sectionTitle || 'this section';
    const prompts = {
      introduction: `Opening for "${label}": context and scope using project name, description, and requirements.`,
      overview: `High-level "${label}" summary tied to project objectives and requirements.`,
      detailed: `Body content for "${label}" using requirement details and project context.`,
      list: `Bulleted or numbered items for "${label}" from structured requirements where applicable.`,
      table: `Tabular "${label}" (rows/columns) populated from requirements and project facts.`,
      conclusion: `Closing "${label}": summary, next steps, or asks using project context.`
    };

    return truncateTemplatizationHint(
      prompts[contentType] || `Content for "${label}" from project and requirements context.`
    );
  }

  // Detect if content appears to be binary data (like from .docx files)
  detectBinaryContent(content) {
    // Check for common binary/XML signatures in Word documents
    const binarySignatures = [
      'PK!', // ZIP signature (docx files are ZIP archives)
      '[Content_Types].xml',
      'word/document.xml',
      'word/_rels/',
      '<?xml version=',
      '\u0000', // NULL bytes
      '\uFFFD', // Replacement character (often from failed binary-to-text conversion)
    ];

    // Check for non-printable characters (common in binary files)
    // eslint-disable-next-line no-control-regex
    const nonPrintableRegex = /[\x00-\x08\x0E-\x1F\x7F-\x9F]/;
    
    return binarySignatures.some(sig => content.includes(sig)) || 
           nonPrintableRegex.test(content) ||
           (content.length > 100 && content.split('').filter(c => (c.charCodeAt(0) < 32 || c.charCodeAt(0) > 126)).length > content.length * 0.3);
  }

  // Create analysis from HTML structure (for Word documents)
  createHtmlBasedAnalysis(htmlContent, textContent, fileName) {
    const structuredFile = { html: htmlContent, text: textContent, fileType: 'word' };

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    // Strategy 1: Look for actual HTML headings first
    const htmlHeadings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

    if (htmlHeadings.length > 0) {
      return this.validateDocumentAnalysis(
        this.processHtmlHeadings(htmlHeadings, doc, fileName, textContent),
        structuredFile
      );
    }

    // Strategy 2: Look for styled elements that might be headings (common with Mammoth.js)
    const potentialHeadings = this.findStyledHeadings(doc);

    if (potentialHeadings.length > 0) {
      return this.validateDocumentAnalysis(
        this.processStyledHeadings(potentialHeadings, doc, fileName, textContent),
        structuredFile
      );
    }

    // Strategy 3: Smart paragraph analysis
    const paragraphs = doc.querySelectorAll('p');

    if (paragraphs.length > 0) {
      return this.validateDocumentAnalysis(
        this.processSmartParagraphs(paragraphs, fileName, textContent),
        structuredFile
      );
    }

    return this.validateDocumentAnalysis(this.createTextBasedStructure(textContent, fileName), structuredFile);
  }

  // Process actual HTML headings
  processHtmlHeadings(headings, doc, fileName, textContent) {
    const structure = [];
    let position = 1;
    
    headings.forEach((heading, idx) => {
      const level = parseInt(heading.tagName.charAt(1));
      const title = heading.textContent.trim();
      const nextHeading = headings[idx + 1] || null;

      if (title && title.length > 2) {
        // Add heading
        structure.push({
          level: Math.min(level, 3),
          type: 'heading',
          title: title,
          htmlTag: `h${Math.min(level, 3)}`,
          content: title,
          contentType: 'heading',
          suggestedPrompt: '',
          position: position++
        });

        // Find content until next heading
        const content = this.getContentUntilNextHeading(heading);
        if (content) {
          const blockTag = this.getFirstBlockTagBetweenHeadings(heading, nextHeading);
          let htmlTag = 'p';
          if (blockTag === 'table') htmlTag = 'table';
          else if (blockTag === 'ul') htmlTag = 'ul';
          else if (blockTag === 'ol') htmlTag = 'ol';

          let contentType = this.analyzeContentType(content);
          if (htmlTag === 'table' && contentType !== 'table') contentType = 'table';
          if ((htmlTag === 'ul' || htmlTag === 'ol') && contentType !== 'list') contentType = 'list';

          structure.push({
            level: Math.min(level + 1, 3),
            type: 'content',
            title: `${title} Content`,
            htmlTag,
            content: content,
            contentType,
            suggestedPrompt: this.createContextualPrompt(contentType, title),
            position: position++
          });
        }
      }
    });
    
    return this.buildAnalysisResult(structure, fileName, textContent);
  }

  // Find headings by styling (bold, larger text, etc.)
  findStyledHeadings(doc) {
    const headings = [];
    const elements = doc.querySelectorAll('p, div, span');
    
    elements.forEach((el) => {
      const text = el.textContent.trim();
      if (text.length < 5 || text.length > 200) return; // Skip very short or very long text
      
      const style = el.style || {};
      const computedStyle = window.getComputedStyle ? window.getComputedStyle(el) : {};
      
      let headingScore = 0;
      
      // Check for bold styling
      if (style.fontWeight === 'bold' || 
          style.fontWeight >= 600 || 
          computedStyle.fontWeight === 'bold' ||
          computedStyle.fontWeight >= 600 ||
          el.tagName === 'STRONG' ||
          el.querySelector('strong, b')) {
        headingScore += 2;
      }
      
      // Check for larger font size
      const fontSize = parseFloat(style.fontSize) || parseFloat(computedStyle.fontSize);
      if (fontSize > 14) headingScore += 1;
      if (fontSize > 16) headingScore += 1;
      
      // Check for heading-like patterns
      if (/^\d+\.?\s/.test(text)) headingScore += 1; // Numbered headings
      if (/^[A-Z][A-Z\s]+$/.test(text)) headingScore += 1; // ALL CAPS
      if (text.endsWith(':')) headingScore += 1; // Ends with colon
      if (!/[.!?]$/.test(text)) headingScore += 1; // Doesn't end with sentence punctuation
      
      // Check position and context
      const nextSibling = el.nextElementSibling;
      if (nextSibling && nextSibling.tagName === 'P') headingScore += 1;
      
      // If it's likely a heading
      if (headingScore >= 3) {
        headings.push({
          element: el,
          text: text,
          score: headingScore
        });
      }
    });
    
    // Sort by score and position
    return headings.sort((a, b) => {
      const posA = Array.from(doc.body.querySelectorAll('*')).indexOf(a.element);
      const posB = Array.from(doc.body.querySelectorAll('*')).indexOf(b.element);
      return posA - posB;
    });
  }

  // Process styled headings
  processStyledHeadings(headings, doc, fileName, textContent) {
    const structure = [];
    let position = 1;
    
    headings.forEach((heading, index) => {
      const title = heading.text;
      const level = Math.min(Math.floor(heading.score / 2), 3); // Convert score to level
      
      // Add heading
      structure.push({
        level: Math.max(level, 1),
        type: 'heading',
        title: title,
        htmlTag: `h${Math.max(level, 1)}`,
        content: title,
        contentType: 'heading',
        suggestedPrompt: '',
        position: position++
      });
      
      // Find content after this heading
      const content = this.getContentAfterElement(heading.element);
      if (content) {
        structure.push({
          level: Math.max(level + 1, 2),
          type: 'content',
          title: `${title} Content`,
          htmlTag: 'p',
          content: content,
          contentType: this.analyzeContentType(content),
          suggestedPrompt: this.createContextualPrompt(this.analyzeContentType(content), title),
          position: position++
        });
      }
    });
    
    return this.buildAnalysisResult(structure, fileName, textContent);
  }

  // Smart paragraph processing
  processSmartParagraphs(paragraphs, fileName, textContent) {
    const structure = [];
    let position = 1;
    let currentSection = [];
    let sectionCount = 1;
    
    paragraphs.forEach((p, index) => {
      const text = p.textContent.trim();
      if (text.length < 20) return; // Skip very short paragraphs
      
      currentSection.push(text);
      
      // Create sections every 3-4 paragraphs or at end
      if (currentSection.length >= 3 || index === paragraphs.length - 1) {
        const combinedContent = currentSection.join(' ');
        const preview = combinedContent.length > 200 ? combinedContent.substring(0, 200) + '...' : combinedContent;
        
        // Try to identify a good title from the first paragraph
        const firstPara = currentSection[0];
        const title = this.extractTitleFromText(firstPara) || `Section ${sectionCount}`;
        
        structure.push({
          level: 1,
          type: 'heading',
          title: title,
          htmlTag: 'h2',
          content: title,
          contentType: 'heading',
          suggestedPrompt: '',
          position: position++
        });
        
        structure.push({
          level: 2,
          type: 'content',
          title: `${title} Content`,
          htmlTag: 'p',
          content: preview,
          contentType: this.analyzeContentType(combinedContent),
          suggestedPrompt: this.createContextualPrompt(this.analyzeContentType(combinedContent), title),
          position: position++
        });
        
        currentSection = [];
        sectionCount++;
      }
    });
    
    return this.buildAnalysisResult(structure, fileName, textContent);
  }

  // Extract potential title from text
  extractTitleFromText(text) {
    // Take first sentence or first 50 characters
    const firstSentence = text.split(/[.!?]/)[0].trim();
    if (firstSentence.length > 5 && firstSentence.length <= 80) {
      return firstSentence;
    }
    
    // Take first 50 characters
    if (text.length > 50) {
      return text.substring(0, 47) + '...';
    }
    
    return text;
  }

  // Get content until next heading
  getContentUntilNextHeading(headingElement) {
    const contentParts = [];
    let nextElement = headingElement.nextElementSibling;
    
    while (nextElement && !nextElement.matches('h1, h2, h3, h4, h5, h6')) {
      if (nextElement.textContent && nextElement.textContent.trim()) {
        const text = nextElement.textContent.trim();
        if (text.length > 10) {
          contentParts.push(text);
        }
      }
      nextElement = nextElement.nextElementSibling;
    }
    
    if (contentParts.length > 0) {
      const content = contentParts.join(' ');
      return content.length > 200 ? content.substring(0, 200) + '...' : content;
    }
    return null;
  }

  // Get content after an element
  getContentAfterElement(element) {
    const contentParts = [];
    let nextElement = element.nextElementSibling;
    let count = 0;
    
    while (nextElement && count < 3) { // Limit to next 3 elements
      if (nextElement.textContent && nextElement.textContent.trim()) {
        const text = nextElement.textContent.trim();
        if (text.length > 10) {
          contentParts.push(text);
          count++;
        }
      }
      nextElement = nextElement.nextElementSibling;
    }
    
    if (contentParts.length > 0) {
      const content = contentParts.join(' ');
      return content.length > 200 ? content.substring(0, 200) + '...' : content;
    }
    return null;
  }

  // Build final analysis result
  buildAnalysisResult(structure, fileName, textContent) {
    return {
      documentTitle: fileName ? fileName.replace(/\.[^/.]+$/, '') : 'Document Template',
      structure: structure,
      metadata: {
        totalSections: structure.length,
        complexity: structure.length > 8 ? 'high' : structure.length > 4 ? 'medium' : 'low',
        documentType: this.inferDocumentType(textContent),
        recommendedTemplateStructure: `Enhanced HTML-based template with ${structure.length} sections extracted from Word document structure`
      }
    };
  }

  // Create structure from plain text when HTML parsing fails
  createTextBasedStructure(textContent, fileName) {
    const lines = textContent.split('\n').filter(line => line.trim());
    const structure = [];
    let position = 1;
    
    // Group lines into sections
    let currentSection = [];
    let sectionCount = 1;
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (trimmed.length < 10) return;
      
      currentSection.push(trimmed);
      
      // Create sections every 5-6 lines or at end
      if (currentSection.length >= 5 || index === lines.length - 1) {
        const combinedContent = currentSection.join(' ');
        const title = this.extractTitleFromText(currentSection[0]) || `Section ${sectionCount}`;
        const preview = combinedContent.length > 200 ? combinedContent.substring(0, 200) + '...' : combinedContent;
        
        structure.push({
          level: 1,
          type: 'heading',
          title: title,
          htmlTag: 'h2',
          content: title,
          contentType: 'heading',
          suggestedPrompt: '',
          position: position++
        });
        
        structure.push({
          level: 2,
          type: 'content',
          title: `${title} Content`,
          htmlTag: 'p',
          content: preview,
          contentType: this.analyzeContentType(combinedContent),
          suggestedPrompt: this.createContextualPrompt(this.analyzeContentType(combinedContent), title),
          position: position++
        });
        
        currentSection = [];
        sectionCount++;
      }
    });
    
    return this.buildAnalysisResult(structure, fileName, textContent);
  }

  /**
   * Analyze large documents chunk by chunk (headings grouped by 25), in stable order.
   * @param {{text: string, html: string}} fileContent - Mammoth output.
   * @param {string} [fileName] - File name.
   * @param {object} [options] - accountId, variant, signal.
   * @returns {Promise<object>} Validated analysis.
   */
  async analyzeDocumentStructureChunked(fileContent, fileName = '', options = {}) {
    const parallelConfig = ANALYSIS_PARALLEL;
    const contentText = fileContent.text;
    const htmlContent = fileContent.html;
    
    const startTime = Date.now();

    
    try {
      // Step 1: Extract and scrub HTML structure
      const cleanedStructure = this.extractAndCleanDocumentStructure(htmlContent);

      
      // Step 2: Group sections into chunks of ~25 headings
      const chunks = this.groupIntoChunks(cleanedStructure.sections, 25);

      
      let allAnalyzedSections = [];
      
      if (parallelConfig.enabled && chunks.length > 1) {
        // Step 3: Process chunks in parallel batches using helper method
  
        
        // Define the processor function for each chunk
        const chunkProcessor = async (chunk, globalIndex) => {
          const result = await this.analyzeDocumentChunk(chunk, globalIndex + 1, chunks.length, options);
          return result.structure; // Return the structure array directly
        };
        
        // Define error handler for failed chunks
        const onItemError = (chunkNumber, error) => {
          console.warn(`Error processing analysis chunk ${chunkNumber}:`, error);
          // Return fallback sections for failed chunk
          const chunkIndex = chunkNumber - 1;
          const chunk = chunks[chunkIndex];
          return this.createFallbackChunkSections(chunk, chunkNumber);
        };
        
        // Process chunks using the parallel batch helper
        const results = await this.processInParallelBatches(
          chunks,
          chunkProcessor,
          {
            batchSize: parallelConfig.batchSize,
            batchDelay: parallelConfig.batchDelay,
            processName: 'analysis chunks',
            onItemError
          }
        );
        
        // Flatten the results array (since each chunk returns an array of sections)
        allAnalyzedSections = results.flat();
      } else {
        // Fall back to sequential processing
  
        
        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];

          
          try {
            const chunkAnalysis = await this.analyzeDocumentChunk(chunk, i + 1, chunks.length, options);
            allAnalyzedSections.push(...chunkAnalysis.structure);
            
            // Small delay between chunks to avoid rate limiting
            if (i < chunks.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } catch (chunkError) {
            console.warn(`Error processing analysis chunk ${i + 1}:`, chunkError);
            // Add fallback sections for failed chunk
            allAnalyzedSections.push(...this.createFallbackChunkSections(chunk, i + 1));
          }
        }
      }
      
      // Step 4: Stitch results together
      const endTime = Date.now();
      const processingTime = (endTime - startTime) / 1000;
      const processingMethod = parallelConfig.enabled && chunks.length > 1 ? 'parallel' : 'sequential';
      
      const finalAnalysis = {
        documentTitle: cleanedStructure.title || (fileName ? fileName.replace(/\.[^/.]+$/, '') : 'Document Template'),
        structure: allAnalyzedSections,
        metadata: {
          totalSections: allAnalyzedSections.length,
          complexity: allAnalyzedSections.length > 50 ? 'high' : allAnalyzedSections.length > 20 ? 'medium' : 'low',
          documentType: this.inferDocumentType(contentText),
          recommendedTemplateStructure: `${processingMethod.charAt(0).toUpperCase() + processingMethod.slice(1)} chunked analysis of large document with ${chunks.length} processing chunks`,
          chunksProcessed: chunks.length,
          originalSize: contentText.length,
          processingMethod: processingMethod,
          processingTime: processingTime
        }
      };

      return this.validateDocumentAnalysis(finalAnalysis, fileContent);
      
    } catch (error) {
      console.error('Chunked analysis failed, falling back to offline analysis:', error);
      return this.createFallbackAnalysis(fileContent, fileName);
    }
  }

  // Extract and clean document structure from HTML
  extractAndCleanDocumentStructure(htmlContent) {

    
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Find document title
    const titleElement = doc.querySelector('h1') || doc.querySelector('title');
    const title = titleElement ? titleElement.textContent.trim() : '';
    
    // Extract all headings with their content
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const sections = [];
    
    headings.forEach((heading, index) => {
      const level = parseInt(heading.tagName.charAt(1));
      const headingText = heading.textContent.trim();
      
      if (headingText && headingText.length > 2) {
        // Get content between this heading and the next
        const content = this.getCleanContentBetweenHeadings(heading, headings[index + 1]);
        const nextH = headings[index + 1] || null;
        const firstBlockTag = this.getFirstBlockTagBetweenHeadings(heading, nextH);

        sections.push({
          level: Math.min(level, 3),
          heading: headingText,
          content: content,
          firstBlockTag,
          originalIndex: index,
          headingElement: heading.tagName.toLowerCase()
        });
      }
    });
    
    return {
      title,
      sections
    };
  }

  // Get clean content between two headings
  getCleanContentBetweenHeadings(currentHeading, nextHeading) {
    const contentParts = [];
    let currentElement = currentHeading.nextElementSibling;
    
    // Collect content until we reach the next heading or end
    while (currentElement && currentElement !== nextHeading) {
      const text = this.normalizeTextForAnalysis(currentElement);
      if (text && text.length > 10) {
        contentParts.push(text);
      }
      currentElement = currentElement.nextElementSibling;
    }
    
    // Limit content length to avoid token issues (large enough for pricing / use-case detail)
    const maxBetween = 6000;
    const fullContent = contentParts.join(' ').trim();
    return fullContent.length > maxBetween ? fullContent.substring(0, maxBetween) + '...' : fullContent;
  }

  /**
   * Normalize element text for analysis (preserve currency, punctuation; drop only control chars).
   * @param {Element} element - DOM element.
   * @returns {string} Normalized text.
   */
  normalizeTextForAnalysis(element) {
    if (!element) return '';

    if (['SCRIPT', 'STYLE', 'META', 'LINK', 'NOSCRIPT'].includes(element.tagName)) {
      return '';
    }

    const text = element.textContent || element.innerText || '';
    return text
      .replace(/\s+/g, ' ')
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .trim();
  }

  // Extract clean text from an element, removing unnecessary formatting
  extractCleanText(element) {
    return this.normalizeTextForAnalysis(element);
  }

  // Group sections into processing chunks
  groupIntoChunks(sections, maxHeadingsPerChunk = 25) {
    const chunks = [];
    let currentChunk = [];
    
    for (const section of sections) {
      currentChunk.push(section);
      
      // Start new chunk when we hit the limit
      if (currentChunk.length >= maxHeadingsPerChunk) {
        chunks.push([...currentChunk]);
        currentChunk = [];
      }
    }
    
    // Add remaining sections as final chunk
    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    
    return chunks;
  }

  /**
   * Analyze one chunk of a large document (task 'template.analyze', schema, per-chunk seed).
   * @param {Array<object>} chunkSections - Sections from extractAndCleanDocumentStructure.
   * @param {number} chunkNumber - 1-based chunk number.
   * @param {number} totalChunks - Chunk count.
   * @param {object} [options] - accountId, variant, signal.
   * @returns {Promise<{structure: Array<object>}>} Chunk analysis.
   */
  async analyzeDocumentChunk(chunkSections, chunkNumber, totalChunks, options = {}) {
    const previewMax = 3500;
    const systemInstruction = `You are an expert at TEMPLATIZATION. This is chunk ${chunkNumber}/${totalChunks} of a large document.

GOAL: Identify sections that should become a reusable document template. When users generate documents later, the app passes NEW project context: project name/description, requirements, meeting summaries, tone — not the text of this gold document.

For each heading: emit a heading row (type "heading", contentType "overview", suggestedPrompt "").
For each content block under a heading: "content" = short excerpt from the source, and "suggestedPrompt" = a SHORT hint (max ~${TEMPLATE_SUGGESTED_PROMPT_MAX_LENGTH} characters) describing what this slot must convey for an arbitrary project. No meta instructions like "Generate a table" or "Draft a paragraph."

CONSTRAINTS:
- htmlTag: h1, h2, h3, p, ul, ol or table (follow the DOM block hint when provided).
- contentType: introduction, overview, detailed, list, table or conclusion.
- Prefer htmlTag "table" for tabular blocks, "ul"/"ol" for lists. Keep source order.

${TEMPLATE_FIELD_RULES}`;

    const chunkContent = chunkSections.map((section, index) => {
      const prev = section.content || '';
      const slice = prev.length > previewMax ? `${prev.substring(0, previewMax)}...` : prev;
      const domHint = section.firstBlockTag ? `DOM block hint: ${section.firstBlockTag}` : '';
      return `Section ${index + 1}
Heading: ${section.heading} (Level ${section.level})
${domHint}
Content (for grounding — use to write excerpt + hint):
${slice}`;
    }).join('\n\n---\n\n');

    const prompt = `Document chunk ${chunkNumber}/${totalChunks} — templatization:

${chunkContent}

Convert these ${chunkSections.length} sections into "structure" rows: headings and templatized content slots with grounded excerpts and short templatization hints.`;

    try {
      const response = await this.runTask({
        task: AI_TASKS.TEMPLATE_ANALYZE,
        profile: 'extract',
        prompt,
        systemInstruction,
        seed: stableSeed(AI_TASKS.TEMPLATE_ANALYZE, fnv1a32Hex(chunkContent), chunkNumber, options.variant ?? 0),
        responseSchema: TEMPLATE_CHUNK_SCHEMA,
        generationConfig: { maxOutputTokens: 16384 },
        accountId: this.resolveAccountId(options.accountId),
        signal: options.signal,
      });
      const structure = Array.isArray(response.json?.structure) ? response.json.structure : null;
      if (!structure) throw new Error('No structure in chunk analysis');
      return { structure };
    } catch (error) {
      console.warn(`Chunk ${chunkNumber} analysis failed:`, error);
      throw error;
    }
  }

  // Create fallback sections for failed chunks
  createFallbackChunkSections(chunkSections, chunkNumber) {
    const fallbackSections = [];
    let position = (chunkNumber - 1) * 50 + 1; // Approximate position
    
    chunkSections.forEach((section) => {
      // Ensure valid htmlTag based on section type
      let htmlTag = 'h2'; // Default for headings
      if (section.headingElement) {
        // Map h4, h5, h6 to h3, and invalid tags to valid ones
        const tag = section.headingElement.toLowerCase();
        if (['h1', 'h2', 'h3'].includes(tag)) {
          htmlTag = tag;
        } else if (['h4', 'h5', 'h6'].includes(tag)) {
          htmlTag = 'h3';
        } else {
          htmlTag = 'h2';
        }
      }
      
      // Add heading
      fallbackSections.push({
        level: Math.min(Math.max(section.level || 2, 1), 3),
        type: 'heading',
        title: section.heading || `Section ${fallbackSections.length + 1}`,
        htmlTag: htmlTag,
        content: section.heading || '',
        contentType: 'overview', // Valid content type for headings
        suggestedPrompt: '',
        position: position++
      });
      
      // Add content if available
      if (section.content) {
        // Determine content type based on content analysis
        const contentType = this.analyzeContentTypeForFallback(section.content);
        let htmlTag = 'p';
        if (section.firstBlockTag === 'table') htmlTag = 'table';
        else if (section.firstBlockTag === 'ul') htmlTag = 'ul';
        else if (section.firstBlockTag === 'ol') htmlTag = 'ol';

        const preview = section.content.length > 800
          ? `${section.content.substring(0, 800)}...`
          : section.content;

        fallbackSections.push({
          level: Math.min(Math.max((section.level || 2) + 1, 2), 3),
          type: 'content',
          title: `${section.heading} Content`,
          htmlTag,
          content: preview,
          contentType: contentType,
          suggestedPrompt: this.createContextualPrompt(contentType, section.heading),
          position: position++
        });
      }
    });
    
    return fallbackSections;
  }

  // Analyze content type for fallback scenarios with valid types only
  analyzeContentTypeForFallback(content) {
    if (!content || content.length < 10) return 'detailed';
    
    const text = content.toLowerCase();
    
    // Check for introduction patterns
    if (text.includes('introduction') || text.includes('overview') || 
        text.includes('summary') || text.includes('background') ||
        text.match(/^(this document|this project|this report)/)) {
      return 'introduction';
    }
    
    // Check for overview patterns
    if (text.includes('scope') || text.includes('objectives') || 
        text.includes('goals') || text.includes('purpose') ||
        text.match(/provides? an? (overview|summary)/)) {
      return 'overview';
    }
    
    // Check for list patterns
    if ((text.match(/\n\s*[-•*]\s/g) || []).length >= 2 || // Multiple bullet points
        (text.match(/\n\s*\d+\.\s/g) || []).length >= 2 || // Multiple numbered items
        text.includes('requirements:') || text.includes('features:') ||
        text.includes('includes:') || text.includes('contains:')) {
      return 'list';
    }
    
    // Check for table patterns
    if (text.includes('|') || text.includes('\t') ||
        text.match(/\b(table|column|row|header)\b/) ||
        text.includes('comparison') || text.includes('specification')) {
      return 'table';
    }
    
    // Check for conclusion patterns
    if (text.includes('conclusion') || text.includes('summary') ||
        text.includes('next steps') || text.includes('recommendations') ||
        text.includes('in summary') || text.includes('to conclude')) {
      return 'conclusion';
    }
    
    // Default to detailed content
    return 'detailed';
  }

  // Detect appropriate HTML tag for content type
  detectContentType(tagName) {
    const contentMap = {
      'ul': 'ul',
      'ol': 'ol',
      'table': 'table',
      'pre': 'pre',
      'blockquote': 'blockquote'
    };
    return contentMap[tagName] || 'p';
  }

  // Create contextual prompt based on content type and section title
  createContextualPrompt(contentType, sectionTitle) {
    return truncateTemplatizationHint(this.createGenericPrompt(contentType, sectionTitle));
  }

  // Create analysis for binary files with helpful guidance
  createBinaryFileAnalysis(fileName) {
    return {
      documentTitle: fileName ? fileName.replace(/\.[^/.]+$/, '') : 'Document Template',
      structure: [
        {
          level: 1,
          type: 'heading',
          title: 'Document Title',
          htmlTag: 'h1',
          content: 'Main document title',
          contentType: 'overview', // Valid content type for headings
          suggestedPrompt: '',
          position: 1
        },
        {
          level: 2,
          type: 'content',
          title: 'Introduction',
          htmlTag: 'p',
          content: 'Document introduction and overview',
          contentType: 'introduction',
          suggestedPrompt: 'Write a comprehensive introduction that provides context and overview based on the project requirements',
          position: 2
        },
        {
          level: 1,
          type: 'heading',
          title: 'Main Content',
          htmlTag: 'h2',
          content: 'Main section header',
          contentType: 'overview', // Valid content type for headings
          suggestedPrompt: '',
          position: 3
        },
        {
          level: 2,
          type: 'content',
          title: 'Details',
          htmlTag: 'p',
          content: 'Detailed content section',
          contentType: 'detailed',
          suggestedPrompt: 'Provide detailed content with specific information and explanations based on the project requirements',
          position: 4
        },
        {
          level: 1,
          type: 'heading',
          title: 'Conclusion',
          htmlTag: 'h2',
          content: 'Conclusion section',
          contentType: 'overview', // Valid content type for headings
          suggestedPrompt: '',
          position: 5
        },
        {
          level: 2,
          type: 'content',
          title: 'Summary',
          htmlTag: 'p',
          content: 'Concluding summary',
          contentType: 'conclusion',
          suggestedPrompt: 'Write a conclusion that summarizes key points and provides next steps based on the requirements',
          position: 6
        }
      ],
      metadata: {
        totalSections: 6,
        complexity: 'medium',
        documentType: 'document',
        recommendedTemplateStructure: 'Basic document template - original file could not be parsed as text. Please use .txt or .md files for better results.',
        fallbackGenerated: true,
        binaryFileDetected: true
      }
    };
  }

  // Enhanced header detection with context awareness
  isLikelyHeader(line, allLines, currentIndex) {
    const trimmed = line.trim();
    
    // Skip very long lines (likely content, not headers)
    if (trimmed.length > 120) return false;
    
    // Skip very short lines (likely not meaningful headers)
    if (trimmed.length < 3) return false;

    // Pattern-based detection
    const patterns = [
      // Numbered sections: "1. Introduction", "2.1 Overview"
      /^\d+\.?\d*\s+[A-Z]/,
      // Roman numerals: "I. Introduction", "II. Analysis" 
      /^[IVX]+\.?\s+[A-Z]/,
      // Letter sections: "A. Overview", "B. Details"
      /^[A-Z]\.?\s+[A-Z]/,
      // All caps (but not too long): "EXECUTIVE SUMMARY"
      /^[A-Z\s]{3,50}$/,
      // Title case: "Executive Summary", "Project Overview"
      /^[A-Z][a-z]+(\s+[A-Z][a-z]+)*$/,
      // Headers with colons: "Overview:", "Summary:"
      /^[A-Z][a-zA-Z\s]+:$/
    ];

    const matchesPattern = patterns.some(pattern => pattern.test(trimmed));
    
    // Context-based validation
    if (matchesPattern) {
      // Check if next line exists and looks like content (longer, lowercase start)
      const nextLine = currentIndex + 1 < allLines.length ? allLines[currentIndex + 1]?.trim() : '';
      const hasContentAfter = nextLine && nextLine.length > 20 && /^[a-z]/.test(nextLine);
      
      // Check if previous line is short or empty (typical before headers)
      const prevLine = currentIndex > 0 ? allLines[currentIndex - 1]?.trim() : '';
      const hasSpaceBefore = !prevLine || prevLine.length < 30;
      
      return hasContentAfter || hasSpaceBefore;
    }
    
        return false;
  }

  // Helper method for processing items in parallel batches with rate limiting
  async processInParallelBatches(items, processor, options = {}) {
    const {
      batchSize = 3,
      batchDelay = 2000,
      processName = 'items',
      onBatchStart = null,
      onItemComplete = null,
      onItemError = null
    } = options;
    
    const allResults = [];
    
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(items.length / batchSize);
      
      if (onBatchStart) {
        onBatchStart(batchNumber, totalBatches, batch.length);
      } else {

      }
      
      // Create promises for this batch
      const batchPromises = batch.map(async (item, batchIndex) => {
        const globalIndex = i + batchIndex;
        try {
          const result = await processor(item, globalIndex);
          if (onItemComplete) {
            onItemComplete(globalIndex + 1, result);
          } else {

          }
          return { success: true, result, index: globalIndex };
        } catch (error) {
          if (onItemError) {
            const fallback = onItemError(globalIndex + 1, error);
            return { success: false, error, index: globalIndex, fallback };
          } else {
            console.error(`✗ ${processName.slice(0, -1)} ${globalIndex + 1} failed:`, error);
            return { success: false, error, index: globalIndex };
          }
        }
      });
      
      // Process batch in parallel using Promise.allSettled
      try {
        const batchResults = await Promise.allSettled(batchPromises);
        
        // Collect results from all promises
        batchResults.forEach((promiseResult) => {
          if (promiseResult.status === 'fulfilled') {
            const itemResult = promiseResult.value;
            if (itemResult.success) {
              allResults.push(itemResult.result);
            } else {
              // Handle failed item
              console.warn(`Item ${itemResult.index + 1} failed:`, itemResult.error?.message || 'Unknown error');
              if (itemResult.fallback !== undefined) {
                allResults.push(itemResult.fallback);
              }
            }
          } else {
            console.error('Promise rejection in batch processing:', promiseResult.reason);
          }
        });
        
        // Add delay between batches to respect rate limits
        if (i + batchSize < items.length) {

          await new Promise(resolve => setTimeout(resolve, batchDelay));
        }
      } catch (error) {
        console.error(`Batch processing error:`, error);
        // Continue with next batch
      }
    }
    
    return allResults;
  }
}

// Create singleton instance
const aiService = new AIService();

export default aiService; 